import * as React from "react"
import { Tooltip } from "antd"
import { cn } from "../../lib/utils"

// Export the new Leaflet-based GeoMap component
export { CustomGeoMap } from "./custom-geo-map"

interface DataPoint {
  [key: string]: string | number
}

interface CustomBarChartProps {
  data: DataPoint[]
  dataKey?: string
  xAxisKey?: string
  yAxisLabel?: string
  xAxisLabel?: string
  className?: string
  height?: number
  barWidth?: number
  minBarHeight?: number
  fill?: string
  showGrid?: boolean
  showTooltip?: boolean
  showLegend?: boolean
  multiBar?: boolean
  enableDualAxis?: boolean  // Enable dual-axis feature (default: false)
  dualAxisThreshold?: number | false  // Disparity ratio to trigger dual-axis (e.g., 10 = 10x difference), false to disable
  forceLowValueKeys?: string[]  // Explicitly specify which keys should be rendered as lines on secondary y-axis
  forceHighValueKeysAsLines?: boolean  // If true, render highValueKeys as lines instead of bars
  isResponsive?: boolean  // Enable horizontal scroll when false (for expanded views)
}

export const CustomBarChart: React.FC<CustomBarChartProps> = ({
  data,
  dataKey,
  xAxisKey,
  yAxisLabel,
  xAxisLabel,
  className,
  height = 400,
  barWidth = 32,
  minBarHeight = 8,
  fill = "hsl(142, 52%, 37%)",
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  multiBar = false,
  enableDualAxis = false,  // Default: false (dual-axis disabled)
  dualAxisThreshold = 10,  // Default: 10x difference triggers dual-axis
  forceLowValueKeys,  // Explicitly specify which keys should be lines on secondary y-axis
  forceHighValueKeysAsLines = false,  // Render highValueKeys as lines instead of bars
  isResponsive = true  // Default: true (responsive, no horizontal scroll)
}) => {
  const [hoveredBar, setHoveredBar] = React.useState<number | null>(null)
  const [hoveredLegend, setHoveredLegend] = React.useState<string | null>(null)
  const [dimensions, setDimensions] = React.useState({ width: 500, height: height })
  const [error, setError] = React.useState<string | null>(null)
  const [brushSelection, setBrushSelection] = React.useState({ start: 0, end: 0.4 }) // Start zoomed in to 40% of data
  const [isDraggingBrush, setIsDraggingBrush] = React.useState<'start' | 'end' | 'middle' | null>(null)
  const [brushContainerWidth, setBrushContainerWidth] = React.useState(0)
  const isScrollingFromBrush = React.useRef(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const brushContainerRef = React.useRef<HTMLDivElement>(null)
  const [enableTransitions, setEnableTransitions] = React.useState(true)

  // Validate data and detect errors
  React.useEffect(() => {
    try {
      setError(null)

      // Check if data exists - don't render chart if no data
      if (!data || !Array.isArray(data)) {
        setError("no-data")
        return
      }

      if (data.length === 0) {
        setError("no-data")
        return
      }

      // Check if data has valid structure
      if (typeof data[0] !== 'object') {
        setError("Invalid data format")
        return
      }

      // Check for numeric values
      const hasNumericData = data.some(item =>
        item && typeof item === 'object' && Object.values(item).some(value => typeof value === 'number' && !isNaN(value))
      )

      if (!hasNumericData) {
        setError("No numeric data found to plot")
        return
      }

      // Check for invalid values
      const hasInvalidValues = data.some(item =>
        item && typeof item === 'object' && Object.values(item).some(value =>
          typeof value === 'number' && (isNaN(value) || !isFinite(value))
        )
      )

      if (hasInvalidValues) {
        setError("Data contains invalid numeric values (NaN or Infinity)")
        return
      }

    } catch (err) {
      setError(`Error processing data: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }, [data])

  // Auto-detect keys
  const detectKeys = React.useMemo(() => {
    if (!data || data.length === 0 || !data[0] || typeof data[0] !== 'object') return { xKey: "", yKeys: [] }

    // Collect all unique keys from ALL data items, not just the first one
    // This handles cases where some items might be missing certain keys
    const allKeysSet = new Set<string>()
    const numericKeysSet = new Set<string>()
    const nonNumericKeysSet = new Set<string>()

    data.forEach(item => {
      if (item && typeof item === 'object') {
        Object.keys(item).forEach(key => {
          allKeysSet.add(key)
          if (typeof item[key] === 'number') {
            numericKeysSet.add(key)
          } else {
            nonNumericKeysSet.add(key)
          }
        })
      }
    })

    const numericKeys = Array.from(numericKeysSet)
    const nonNumericKeys = Array.from(nonNumericKeysSet)
    const allKeys = Array.from(allKeysSet)

    return {
      xKey: xAxisKey || nonNumericKeys[0] || allKeys[0],
      yKeys: multiBar ? numericKeys : [dataKey || numericKeys[0]]
    }
  }, [data, xAxisKey, dataKey, multiBar])

  const { xKey, yKeys } = detectKeys

  // Auto-generate y-axis label from keys if not provided
  const autoYAxisLabel = React.useMemo(() => {
    if (yAxisLabel) return yAxisLabel
    if (!yKeys || yKeys.length === 0) return ""

    // Don't show y-axis label for multi-bar charts
    if (multiBar) return ""

    // If single key, use it as label
    if (yKeys.length === 1 && yKeys[0]) {
      return yKeys[0]
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    return ""
  }, [yAxisLabel, yKeys, multiBar])

  // Auto-generate X-axis label if not provided
  const autoXAxisLabel = React.useMemo(() => {
    if (xAxisLabel) return xAxisLabel
    if (!xKey) return ""

    // Check if x-axis data appears to be dates
    if (data && data.length > 0) {
      const firstValue = String(data[0][xKey])
      // Check for common date patterns including date ranges
      const isDate = firstValue.match(/^\d{4}-\d{2}-\d{2}/) ||
        firstValue.match(/^\d{2}\/\d{2}\/\d{4}/) ||
        firstValue.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i) ||
        firstValue.match(/^\d{4}-\d{2}-\d{2}\s+to\s+\d{4}-\d{2}-\d{2}$/)

      // Don't show label for date axes
      if (isDate) return ""
    }

    return xKey
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  }, [xAxisLabel, xKey, data])

  // 🔥 ResizeObserver for responsiveness
  React.useEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setDimensions({
            width: entry.contentRect.width,
            height,
          })
        }
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [height])

  // Detect value disparity and split into dual-axis groups if needed
  const { useDualAxis, highValueKeys, lowValueKeys, maxHighValue, maxLowValue } = React.useMemo(() => {
    // If dual-axis is disabled via prop, threshold set to false, or single series, use single axis
    // BUT: Allow dual-axis even with !multiBar if forceLowValueKeys is provided
    // AND: Allow dual-axis even with yKeys.length <= 1 if forceLowValueKeys is provided
    if (!enableDualAxis || dualAxisThreshold === false || (!multiBar && (!forceLowValueKeys || forceLowValueKeys.length === 0)) || (yKeys.length <= 1 && (!forceLowValueKeys || forceLowValueKeys.length === 0))) {
      const max = Array.isArray(data) ? data.reduce((maxVal, item) => {
        const itemMax = yKeys.reduce((keyMax, key) => Math.max(keyMax, (item[key] as number) || 0), 0)
        return Math.max(maxVal, itemMax)
      }, 0) : 0
      return {
        useDualAxis: false,
        highValueKeys: yKeys,
        lowValueKeys: [],
        maxHighValue: Math.max(Math.ceil(max * 1.1), 10),
        maxLowValue: 0
      }
    }

    // If forceLowValueKeys is provided, use explicit key assignment
    if (forceLowValueKeys && forceLowValueKeys.length > 0) {
      // Don't filter forceLowValueKeys by yKeys - use them directly
      // This allows specifying keys that might not be in the auto-detected yKeys
      const lowKeys = forceLowValueKeys
      const highKeys = yKeys.filter(key => !lowKeys.includes(key))

      // Calculate max values for each group
      const maxHigh = Array.isArray(data) ? data.reduce((maxVal, item) => {
        const itemMax = highKeys.reduce((keyMax, key) => Math.max(keyMax, (item[key] as number) || 0), 0)
        return Math.max(maxVal, itemMax)
      }, 0) : 0

      const maxL = Array.isArray(data) ? data.reduce((maxVal, item) => {
        const itemMax = lowKeys.reduce((keyMax, key) => Math.max(keyMax, (item[key] as number) || 0), 0)
        return Math.max(maxVal, itemMax)
      }, 0) : 0

      return {
        useDualAxis: true,
        highValueKeys: highKeys,
        lowValueKeys: lowKeys,
        maxHighValue: Math.max(Math.ceil(maxHigh * 1.1), 10),
        maxLowValue: Math.max(Math.ceil(maxL * 1.1), 10)
      }
    }

    // Calculate average value for each series
    const seriesStats = yKeys.map(key => {
      const values = Array.isArray(data) ? data.map(item => (item[key] as number) || 0).filter(v => v > 0) : []
      const sum = values.reduce((a, b) => a + b, 0)
      const avg = sum / values.length
      const max = Math.max(...values)
      return { key, avg, max }
    })

    // Find the series with max average
    const maxAvg = Math.max(...seriesStats.map(s => s.avg))

    // Check if disparity exceeds threshold
    const hasLargeDisparity = seriesStats.some(s => s.avg > 0 && maxAvg / s.avg > dualAxisThreshold)

    if (!hasLargeDisparity) {
      const max = Math.max(...seriesStats.map(s => s.max))
      return {
        useDualAxis: false,
        highValueKeys: yKeys,
        lowValueKeys: [],
        maxHighValue: Math.max(Math.ceil(max * 1.1), 10), 
        maxLowValue: 0
      }
    }

    // Split into high and low value groups
    const threshold = maxAvg / dualAxisThreshold
    const high = seriesStats.filter(s => s.avg >= threshold).map(s => s.key)
    const low = seriesStats.filter(s => s.avg < threshold).map(s => s.key)

    const maxHigh = Math.max(...seriesStats.filter(s => high.includes(s.key)).map(s => s.max))
    const maxL = Math.max(...seriesStats.filter(s => low.includes(s.key)).map(s => s.max))

    return {
      useDualAxis: true,
      highValueKeys: high,
      lowValueKeys: low,
      maxHighValue: Math.max(Math.ceil(maxHigh * 1.1), 10), 
      maxLowValue: Math.max(Math.ceil(maxL * 1.1), 10)
    }
  }, [data, yKeys, multiBar, enableDualAxis, dualAxisThreshold, forceLowValueKeys])

  const maxValue = maxHighValue

  const margins = {
    top: 20,
    right: useDualAxis ? 60 : 20,
    bottom: (xAxisLabel || autoXAxisLabel) ? 100 : 80,
    left: Math.max((yAxisLabel || autoYAxisLabel) ? 80 : 60, 50)
  }
  const chartWidth = dimensions.width - margins.left - margins.right
  const chartHeight = height - margins.top - margins.bottom

  // Calculate minimum width needed for bars
  const MIN_BAR_WIDTH = 24 // Minimum bar width in pixels for single bars
  const MIN_MULTI_BAR_WIDTH = 14 // Minimum width for each bar in multi-bar charts

  // For multi-bar, calculate based on number of bars per group
  let barsPerGroup = 1
  if (multiBar) {
    const keysForBarWidth = useDualAxis ? highValueKeys : yKeys
    barsPerGroup = keysForBarWidth.length || 1
  }
  const minGroupWidth = multiBar ? (MIN_MULTI_BAR_WIDTH * barsPerGroup + 8) : MIN_BAR_WIDTH

  const minChartWidth = data.length * (minGroupWidth + 8) // 8px spacing between groups
  // Enable brush when chart needs more space (we'll use zoom instead of scroll)
  const needsScroll = minChartWidth > chartWidth
  const effectiveChartWidth = chartWidth // Always use viewport width, zoom via data filtering

  // Filter visible data based on brush selection (zoom level)
  const visibleData = React.useMemo(() => {
    if (!needsScroll) return data

    const brushWidth = brushSelection.end - brushSelection.start
    const startIndex = Math.floor(brushSelection.start * data.length)
    const endIndex = Math.ceil(brushSelection.end * data.length)

    return data.slice(startIndex, endIndex)
  }, [data, brushSelection.start, brushSelection.end, needsScroll])

  // No scroll sync needed - we're using data filtering for zoom

  // Enforce maximum brush width to prevent bar overlap
  React.useEffect(() => {
    if (!data || data.length === 0 || dimensions.width === 0) return

    // Calculate maximum allowed data points based on available width
    // Use very conservative spacing to ensure no overlap at max zoom out
    const spacingPerGroup = multiBar ? 32 : 24 // Large spacing between groups
    const widthPerGroup = multiBar
      ? (MIN_MULTI_BAR_WIDTH * barsPerGroup) + spacingPerGroup
      : MIN_BAR_WIDTH + spacingPerGroup
    const maxVisibleDataPoints = Math.min(
      data.length,
      Math.max(multiBar ? 6 : 10, Math.floor((dimensions.width * 0.75) / widthPerGroup)) // Use only 75% of capacity
    )
    const maxBrushWidth = maxVisibleDataPoints / data.length

    setBrushSelection(prev => {
      const currentWidth = prev.end - prev.start
      // If current selection is wider than maximum allowed, clamp it
      if (currentWidth > maxBrushWidth) {
        // Keep the start position if possible, adjust end
        if (prev.start + maxBrushWidth <= 1) {
          return { start: prev.start, end: prev.start + maxBrushWidth }
        } else {
          // If we can't keep start, anchor to end
          return { start: 1 - maxBrushWidth, end: 1 }
        }
      }
      return prev
    })
  }, [data, dimensions.width, multiBar, barsPerGroup])

  // Measure brush container width
  React.useEffect(() => {
    if (!brushContainerRef.current) return

    const updateBrushWidth = () => {
      if (brushContainerRef.current) {
        setBrushContainerWidth(brushContainerRef.current.offsetWidth)
      }
    }

    updateBrushWidth()

    const observer = new ResizeObserver(updateBrushWidth)
    observer.observe(brushContainerRef.current)

    return () => observer.disconnect()
  }, [needsScroll])

  // Brush interaction handlers
  React.useEffect(() => {
    if (!isDraggingBrush) {
      // Re-enable transitions after dragging stops
      const timer = setTimeout(() => setEnableTransitions(true), 50)
      return () => clearTimeout(timer)
    }

    // Disable transitions while dragging for smooth performance
    setEnableTransitions(false)

    const handleMouseMove = (e: MouseEvent) => {
      if (!brushContainerRef.current) return

      const rect = brushContainerRef.current.getBoundingClientRect()
      const brushWidth = rect.width
      const x = Math.max(0, Math.min(e.clientX - rect.left, brushWidth))
      const ratio = x / brushWidth

      // Calculate maximum visible data points to prevent bar overlap
      // Use very conservative spacing to ensure no overlap at max zoom out
      const spacingPerGroup = multiBar ? 32 : 24 // Large spacing between groups
      const widthPerGroup = multiBar
        ? (MIN_MULTI_BAR_WIDTH * barsPerGroup) + spacingPerGroup
        : MIN_BAR_WIDTH + spacingPerGroup
      const maxVisibleDataPoints = Math.min(
        data.length,
        Math.max(multiBar ? 6 : 10, Math.floor((dimensions.width * 0.75) / widthPerGroup)) // Use only 75% of capacity
      )
      const maxBrushWidth = maxVisibleDataPoints / data.length
      const minBrushWidth = Math.max(0.05, 3 / data.length) // Minimum 3 data points or 5%

      setBrushSelection(prev => {
        if (isDraggingBrush === 'start') {
          // Drag left handle - zoom in/out from left
          const currentWidth = prev.end - prev.start
          const newStart = Math.max(0, Math.min(ratio, prev.end - minBrushWidth))
          const newWidth = prev.end - newStart
          // Don't allow width to exceed maximum
          if (newWidth > maxBrushWidth) {
            return { start: prev.end - maxBrushWidth, end: prev.end }
          }
          return { start: newStart, end: prev.end }
        } else if (isDraggingBrush === 'end') {
          // Drag right handle - zoom in/out from right
          const newEnd = Math.max(prev.start + minBrushWidth, Math.min(ratio, 1))
          const newWidth = newEnd - prev.start
          // Don't allow width to exceed maximum
          if (newWidth > maxBrushWidth) {
            return { start: prev.start, end: prev.start + maxBrushWidth }
          }
          return { start: prev.start, end: newEnd }
        } else if (isDraggingBrush === 'middle') {
          // Drag from middle - pan through data (keep zoom level constant)
          const width = prev.end - prev.start
          let newStart = ratio - width / 2

          // Clamp to valid range
          newStart = Math.max(0, Math.min(newStart, 1 - width))
          return { start: newStart, end: newStart + width }
        }
        return prev
      })
    }

    const handleMouseUp = () => {
      setIsDraggingBrush(null)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDraggingBrush, dimensions.width])

  // Add padding to prevent first bar from touching Y-axis
  const BAR_LEFT_PADDING = 8
  const availableChartWidth = effectiveChartWidth - BAR_LEFT_PADDING
  const barGroupWidth = availableChartWidth / visibleData.length

  let barActualWidth: number
  let barSpacing: number
  let groupSpacing = 0 // Space between bar groups

  if (multiBar) {
    const keysForBarWidth = useDualAxis ? highValueKeys : yKeys
    const numBars = keysForBarWidth.length

    // Calculate ideal bar width based on available space
    // Reserve 20% of group width for spacing between groups
    const groupContentWidth = barGroupWidth * 0.8
    const idealBarWidth = groupContentWidth / numBars

    // If bars are getting too narrow, reduce group spacing
    if (idealBarWidth < MIN_MULTI_BAR_WIDTH) {
      // Use minimum bar width and pack bars tightly
      barActualWidth = MIN_MULTI_BAR_WIDTH
      barSpacing = MIN_MULTI_BAR_WIDTH
      groupSpacing = Math.max(2, (barGroupWidth - (numBars * MIN_MULTI_BAR_WIDTH)) / 2)
    } else {
      // Normal case - use calculated width with small gaps between bars
      barActualWidth = Math.min(idealBarWidth * 0.95, barWidth)
      barSpacing = barActualWidth
      groupSpacing = (barGroupWidth - (numBars * barActualWidth)) / 2
    }
  } else {
    barActualWidth = Math.min(barWidth, barGroupWidth * 0.8)
    barSpacing = barActualWidth
  }

  const getBarX = (index: number, keyIndex: number = 0) => {
    const baseX = needsScroll ? 0 : margins.left
    const groupX = baseX + BAR_LEFT_PADDING + index * barGroupWidth

    if (!multiBar) return groupX + barGroupWidth / 2 - barActualWidth / 2

    const keysForBarWidth = useDualAxis ? highValueKeys : yKeys
    // Start from left edge of group plus spacing, then add bar positions
    return groupX + groupSpacing + keyIndex * barSpacing
  }

  const getBarHeight = (value: number) => {
    // For zero, null or undefined values, still show minimum height
    if (value === 0 || value === null || value === undefined) return minBarHeight
    const calculatedHeight = (value / maxValue) * chartHeight
    // Ensure even small values are visible
    return Math.max(calculatedHeight, minBarHeight)
  }

  const getBarY = (value: number) => margins.top + chartHeight - getBarHeight(value)

  // Helper functions for right Y-axis (low values)
  const getLineY = (value: number) => {
    if (!useDualAxis || maxLowValue === 0) return 0
    const ratio = value / maxLowValue
    return margins.top + chartHeight * (1 - ratio)
  }

  const colors = [
    "hsl(142, 52%, 37%)",  // Green
    "hsl(24, 100%, 50%)",   // Orange
    "hsl(210, 100%, 50%)",  // Blue
    "hsl(340, 82%, 52%)",   // Pink/Red
    "hsl(45, 93%, 47%)",    // Yellow/Gold
    "hsl(271, 76%, 53%)",   // Purple
    "hsl(184, 77%, 34%)",   // Teal
    "hsl(320, 70%, 40%)"    // Magenta
  ]

  const yTicks = React.useMemo(() => {
    const tickCount = 5
    return Array.from({ length: tickCount + 1 }, (_, i) => (maxValue / tickCount) * i)
  }, [maxValue])

  const yTicksRight = React.useMemo(() => {
    if (!useDualAxis) return []
    const tickCount = 5
    return Array.from({ length: tickCount + 1 }, (_, i) => (maxLowValue / tickCount) * i)
  }, [useDualAxis, maxLowValue])

  const xTicks = React.useMemo(() => {
    if (!Array.isArray(data)) return []
    const baseX = needsScroll ? 0 : margins.left
    return data.map((_, i) => baseX + i * barGroupWidth + barGroupWidth / 2)
  }, [data, margins.left, barGroupWidth, needsScroll])

  const formatValue = (value: number | undefined, useAbbreviation: boolean = true) => {
    if (value === undefined || value === null) return '0'
    if (useAbbreviation) {
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
      // For values less than 1, show 2 decimal places; otherwise show appropriate decimals
      if (value < 1 && value > 0) return value.toFixed(2)
      if (value < 10) return value.toFixed(1)
      return value.toFixed(0)
    }
    // For tooltip (not abbreviated), show more precision for small values
    if (value < 1 && value > 0) return value.toFixed(2)
    if (value < 10) return value.toFixed(1)
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
  }

  const formatXLabel = (value: string | number | undefined) => {
    if (value === undefined || value === null) return ''
    const str = String(value)

    // Handle date range format "YYYY-MM-DD to YYYY-MM-DD"
    if (str.match(/^\d{4}-\d{2}-\d{2}\s+to\s+\d{4}-\d{2}-\d{2}$/)) {
      const dates = str.split(' to ')
      const startDate = new Date(dates[0])
      const endDate = new Date(dates[1])

      // Format as "Sep 17-23" if same month, or "Sep 17 - Sep 23" if different months
      const startMonth = startDate.toLocaleDateString("en-US", { month: "short" })
      const endMonth = endDate.toLocaleDateString("en-US", { month: "short" })
      const startDay = startDate.getDate()
      const endDay = endDate.getDate()

      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}`
      } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
      }
    }

    // Handle single date format
    if (str.match(/^\d{4}-\d{2}-\d{2}/)) {
      const date = new Date(str)
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    }

    return str.length > 10 ? str.substring(0, 10) + "..." : str
  }

  // Don't render chart until we have dimensions
  if (dimensions.width === 0) {
    return (
      <div ref={containerRef} className={cn("w-full", className)} style={{ minHeight: height }}>
        <div className="flex items-center justify-center" style={{ height: height }}>
          <div className="text-gray-400">Loading chart...</div>
        </div>
      </div>
    );
  }

  // Don't render anything if there's an error (except for specific environments)
  if (error) {
    // Only show error messages when using the gaggle.online base URL (development/staging)
    const isDevelopment = window.location.hostname.includes('localhost') ||
                          window.location.hostname.includes('gaggle.online')

    if (isDevelopment && error !== 'no-data') {
      return (
        <div ref={containerRef} className={cn("w-full bg-red-50 border border-red-200 rounded-lg p-4", className)} style={{ minHeight: height }}>
          <div className="flex items-center justify-center flex-col gap-2" style={{ height: height }}>
            <div className="text-red-600 font-semibold">Chart Error</div>
            <div className="text-red-500 text-sm">{error}</div>
            <div className="text-gray-500 text-xs mt-2">This error is only visible in development mode</div>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      <div className="relative flex">
        {/* Sticky Left Y-axis section when scrollable */}
        {needsScroll && (
          <div className="flex-shrink-0 sticky left-0 z-10 bg-white" style={{ width: margins.left }}>
            <svg width={margins.left} height={height}>
              {/* Y-axis line */}
              <line
                x1={margins.left - 1}
                y1={margins.top}
                x2={margins.left - 1}
                y2={margins.top + chartHeight}
                stroke="#D5D7DA"
                strokeWidth="2"
              />
              {/* Y-axis ticks and labels */}
              {yTicks.map((tick, i) => (
                <text
                  key={i}
                  x={margins.left - 12}
                  y={margins.top + chartHeight - (tick / maxValue) * chartHeight + 5}
                  textAnchor="end"
                  className="fill-muted-foreground text-xs"
                >
                  {formatValue(tick)}
                </text>
              ))}
              {/* Y-axis label */}
              {autoYAxisLabel && (
                <text
                  x={-(margins.top + chartHeight / 2)}
                  y={margins.left - 60}
                  transform="rotate(-90)"
                  textAnchor="middle"
                  className="fill-muted-foreground text-sm font-medium"
                >
                  {autoYAxisLabel}
                </text>
              )}
            </svg>
          </div>
        )}

        {/* Chart area with zoom */}
        <div className="relative flex-1">
          <svg width={dimensions.width} height={height} className="overflow-visible">
            {/* Horizontal grid lines */}
            {showGrid && (
              <g>
                {yTicks.map((tick, i) => {
                  const y = margins.top + chartHeight - (tick / maxValue) * chartHeight
                  const startX = needsScroll ? 0 : margins.left
                  return (
                    <line
                      key={i}
                      x1={startX}
                      y1={y}
                      x2={startX + effectiveChartWidth}
                      y2={y}
                      stroke="#D5D7DA"
                      strokeWidth="1"
                      opacity="0.2"
                    />
                  )
                })}
              </g>
            )}

            {/* Vertical grid lines */}
            {showGrid && (
              <g>
                {xTicks.map((tick, i) => {
                  const adjustedTick = needsScroll ? tick - margins.left : tick
                  return (
                    <line
                      key={i}
                      x1={adjustedTick}
                      y1={margins.top}
                      x2={adjustedTick}
                      y2={margins.top + chartHeight}
                      stroke="hsl(var(--border))"
                      strokeWidth="1"
                      opacity="0.2"
                    />
                  )
                })}
              </g>
            )}

            {/* Y-axis (only show when not scrollable, otherwise it's in sticky section) */}
            {!needsScroll && (
              <g>
                <line
                  x1={margins.left}
                  y1={margins.top}
                  x2={margins.left}
                  y2={margins.top + chartHeight}
                  stroke="#D5D7DA"
                  strokeWidth="2"
                />
                {yTicks.map((tick, i) => (
                  <text
                    key={i}
                    x={margins.left - 10}
                    y={margins.top + chartHeight - (tick / maxValue) * chartHeight + 5}
                    textAnchor="end"
                    className="fill-muted-foreground text-xs"
                  >
                    {formatValue(tick)}
                  </text>
                ))}
                {autoYAxisLabel && (
                  <text
                    x={-(margins.top + chartHeight / 2)}
                    y={margins.left - 60}
                    transform="rotate(-90)"
                    textAnchor="middle"
                    className="fill-muted-foreground text-sm font-medium"
                  >
                    {autoYAxisLabel}
                  </text>
                )}
              </g>
            )}

            {/* Right Y-axis (for low values in dual-axis mode) - only show when not scrollable */}
            {useDualAxis && !needsScroll && (
              <g>
                <line
                  x1={margins.left + effectiveChartWidth}
                  y1={margins.top}
                  x2={margins.left + effectiveChartWidth}
                  y2={margins.top + chartHeight}
                  stroke="#D5D7DA"
                  strokeWidth="2"
                />
                {yTicksRight.map((tick, i) => {
                  const y = margins.top + chartHeight - (tick / maxLowValue) * chartHeight
                  return (
                    <text
                      key={i}
                      x={margins.left + effectiveChartWidth + 10}
                      y={y + 5}
                      textAnchor="start"
                      className="fill-muted-foreground text-xs"
                    >
                      {formatValue(tick)}
                    </text>
                  )
                })}
              </g>
            )}

            {/* X-axis */}
            <g>
              <line
                x1={needsScroll ? 0 : margins.left}
                y1={margins.top + chartHeight}
                x2={(needsScroll ? 0 : margins.left) + effectiveChartWidth}
                y2={margins.top + chartHeight}
                stroke="#D5D7DA"
                strokeWidth="2"
              />
              {Array.isArray(visibleData) && visibleData.map((item, i) => {
                const baseX = needsScroll ? 0 : margins.left
                const x = baseX + BAR_LEFT_PADDING + i * barGroupWidth + barGroupWidth / 2
                const y = margins.top + chartHeight + 20
                // Show all labels since we've limited max zoom to prevent overlap
                return (
                  <text
                    key={i}
                    transform={`translate(${x}, ${y}) rotate(-45)`}
                    textAnchor="end"
                    className="fill-muted-foreground text-[10px] sm:text-xs"
                    style={{ fontSize: dimensions.width < 640 ? '9px' : undefined }}
                  >
                    {formatXLabel(item[xKey])}
                  </text>
                )
              })}
            </g>

            {/* Bars (or Lines if forceHighValueKeysAsLines is true) */}
            {!forceHighValueKeysAsLines && (
              <g>
                {Array.isArray(visibleData) && visibleData.map((item, dataIndex) => (
                  <g key={`${item[xKey]}-${dataIndex}`}>
                    {highValueKeys.map((key, keyIndex) => {
                    const value = item[key] as number
                    const barX = getBarX(dataIndex, keyIndex)
                    const barY = getBarY(value)
                    const barHeight = getBarHeight(value)
                    const color = multiBar ? colors[keyIndex % colors.length] : fill
                    const legendOpacity = hoveredLegend && hoveredLegend !== key ? 0.3 : 1
                    const barHoverOpacity = hoveredBar === null || hoveredBar === dataIndex ? 1 : 0.5
                    const finalOpacity = Math.min(legendOpacity, barHoverOpacity)

                    return (
                      <g key={`${dataIndex}-${keyIndex}`}>
                        <Tooltip
                          title={
                            <div>
                              <div style={{ marginBottom: 4, color: '#9ca3af', fontSize: 12 }}>
                                {(() => {
                                  const xLabelFull = String(item[xKey])

                                  // Handle date range format
                                  if (xLabelFull.match(/^\d{4}-\d{2}-\d{2}\s+to\s+\d{4}-\d{2}-\d{2}$/)) {
                                    const dates = xLabelFull.split(' to ')
                                    const startDate = new Date(dates[0])
                                    const endDate = new Date(dates[1])

                                    const startFormatted = startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                    const endFormatted = endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })

                                    // If same year, show it once
                                    if (startDate.getFullYear() === endDate.getFullYear()) {
                                      const startWithoutYear = startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                      const endWithoutYear = endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                      return `${startWithoutYear} - ${endWithoutYear}, ${startDate.getFullYear()}`
                                    }
                                    return `${startFormatted} - ${endFormatted}`
                                  }

                                  // Handle single date format
                                  return xLabelFull.match(/^\d{4}-\d{2}-\d{2}/)
                                    ? new Date(xLabelFull).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                    : xLabelFull
                                })()}
                              </div>
                              <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
                                {String(key).replace(/_/g, " ").charAt(0).toUpperCase() + String(key).replace(/_/g, " ").slice(1)}
                              </div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>
                                {formatValue(value, false)}
                              </div>
                            </div>
                          }
                          placement="top"
                          overlayClassName="custom-chart-tooltip"
                        >
                          <path
                            d={`
                            M ${barX} ${barY + 4}
                            Q ${barX} ${barY}, ${barX + 4} ${barY}
                            L ${barX + (multiBar ? barSpacing * 0.8 : barActualWidth) - 4} ${barY}
                            Q ${barX + (multiBar ? barSpacing * 0.8 : barActualWidth)} ${barY}, ${barX + (multiBar ? barSpacing * 0.8 : barActualWidth)} ${barY + 4}
                            L ${barX + (multiBar ? barSpacing * 0.8 : barActualWidth)} ${barY + barHeight}
                            L ${barX} ${barY + barHeight}
                            Z
                          `}
                            fill={color}
                            className="cursor-pointer"
                            style={{
                              opacity: finalOpacity,
                              transition: enableTransitions ? 'opacity 0.2s ease-out' : 'none'
                            }}
                            onMouseEnter={() => setHoveredBar(dataIndex)}
                            onMouseLeave={() => setHoveredBar(null)}
                          />
                        </Tooltip>
                      </g>
                    )
                    })}
                  </g>
                ))}
              </g>
            )}

            {/* Lines for high value keys (when forceHighValueKeysAsLines is true) */}
            {forceHighValueKeysAsLines && highValueKeys.length > 0 && (
              <g>
                {highValueKeys.map((key, keyIndex) => {
                  const lineColor = colors[keyIndex % colors.length]
                  const legendOpacity = hoveredLegend && hoveredLegend !== key ? 0.3 : 1

                  // Generate path for the line using primary y-axis (getBarY)
                  const pathData = visibleData.map((item, dataIndex) => {
                    const baseX = needsScroll ? 0 : margins.left
                    const x = baseX + BAR_LEFT_PADDING + dataIndex * barGroupWidth + barGroupWidth / 2
                    const value = item[key] as number
                    const y = getBarY(value || 0) // Use primary y-axis
                    return { x, y, value, item }
                  }).filter(point => point.value !== null && point.value !== undefined)

                  // Create smooth curved path
                  let path = ''
                  if (pathData.length > 0) {
                    path = `M ${pathData[0].x} ${pathData[0].y}`

                    if (pathData.length === 2) {
                      path += ` L ${pathData[1].x} ${pathData[1].y}`
                    } else {
                      const maxY = margins.top + chartHeight
                      for (let i = 0; i < pathData.length - 1; i++) {
                        const p0 = i > 0 ? pathData[i - 1] : pathData[i]
                        const p1 = pathData[i]
                        const p2 = pathData[i + 1]
                        const p3 = i < pathData.length - 2 ? pathData[i + 2] : pathData[i + 1]

                        const tension = 0.4
                        const t1x = tension * (p2.x - p0.x)
                        const t1y = tension * (p2.y - p0.y)
                        const t2x = tension * (p3.x - p1.x)
                        const t2y = tension * (p3.y - p1.y)

                        let cp1x = p1.x + t1x / 3
                        let cp1y = p1.y + t1y / 3
                        let cp2x = p2.x - t2x / 3
                        let cp2y = p2.y - t2y / 3

                        cp1y = Math.min(cp1y, maxY)
                        cp2y = Math.min(cp2y, maxY)

                        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
                      }
                    }
                  }

                  return (
                    <g key={key}>
                      <path
                        d={path}
                        fill="none"
                        stroke={lineColor}
                        strokeWidth={2}
                        style={{
                          opacity: legendOpacity,
                          transition: enableTransitions ? 'opacity 0.2s ease-out' : 'none'
                        }}
                      />
                      {/* Dots on line */}
                      {pathData.map((point, idx) => (
                        <Tooltip
                          key={`${point.item[xKey]}-${idx}`}
                          title={
                            <div>
                              <div style={{ marginBottom: 4, color: '#9ca3af', fontSize: 12 }}>
                                {String(point.item[xKey])}
                              </div>
                              <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
                                {String(key).replace(/_/g, " ").charAt(0).toUpperCase() + String(key).replace(/_/g, " ").slice(1)}
                              </div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>
                                {formatValue(point.value, false)}
                              </div>
                            </div>
                          }
                          placement="top"
                          overlayClassName="custom-chart-tooltip"
                        >
                          <circle
                            cx={point.x}
                            cy={point.y}
                            r={4}
                            fill={lineColor}
                            className="cursor-pointer"
                            style={{
                              opacity: legendOpacity,
                              transition: enableTransitions ? 'opacity 0.2s ease-out' : 'none'
                            }}
                          />
                        </Tooltip>
                      ))}
                    </g>
                  )
                })}
              </g>
            )}

            {/* Lines (for low values in dual-axis mode) */}
            {useDualAxis && lowValueKeys.length > 0 && (
              <g>
                {lowValueKeys.map((key, keyIndex) => {
                  const lineColor = colors[(highValueKeys.length + keyIndex) % colors.length]
                  const legendOpacity = hoveredLegend && hoveredLegend !== key ? 0.3 : 1

                  // Generate path for the line
                  const pathData = visibleData.map((item, dataIndex) => {
                    const baseX = needsScroll ? 0 : margins.left
                    const x = baseX + BAR_LEFT_PADDING + dataIndex * barGroupWidth + barGroupWidth / 2
                    const value = item[key] as number
                    const y = getLineY(value || 0)
                    return { x, y, value, item }
                  }).filter(point => point.value !== null && point.value !== undefined)

                  // Create smooth curved path using cubic Bézier curves
                  let path = ''
                  if (pathData.length > 0) {
                    path = `M ${pathData[0].x} ${pathData[0].y}`

                    if (pathData.length === 2) {
                      // For just 2 points, use straight line
                      path += ` L ${pathData[1].x} ${pathData[1].y}`
                    } else {
                      // For 3+ points, use smooth curves
                      const maxY = margins.top + chartHeight // Y position for value 0

                      for (let i = 0; i < pathData.length - 1; i++) {
                        const p0 = i > 0 ? pathData[i - 1] : pathData[i]
                        const p1 = pathData[i]
                        const p2 = pathData[i + 1]
                        const p3 = i < pathData.length - 2 ? pathData[i + 2] : pathData[i + 1]

                        // Calculate tangents using cardinal splines with tension
                        const tension = 0.4
                        const t1x = tension * (p2.x - p0.x)
                        const t1y = tension * (p2.y - p0.y)
                        const t2x = tension * (p3.x - p1.x)
                        const t2y = tension * (p3.y - p1.y)

                        // Control points
                        let cp1x = p1.x + t1x / 3
                        let cp1y = p1.y + t1y / 3
                        let cp2x = p2.x - t2x / 3
                        let cp2y = p2.y - t2y / 3

                        cp1y = Math.min(cp1y, maxY)
                        cp2y = Math.min(cp2y, maxY)

                        const clampedP2y = Math.min(p2.y, maxY)

                        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${clampedP2y}`
                      }
                    }
                  }

                  return (
                    <g key={`line-${keyIndex}`}>
                      {/* Line path */}
                      <path
                        d={path}
                        fill="none"
                        stroke={lineColor}
                        strokeWidth="2"
                        style={{
                          opacity: legendOpacity,
                          transition: enableTransitions ? 'opacity 0.2s ease-out' : 'none'
                        }}
                      />

                      {/* Data points */}
                      {pathData.map((point, pointIndex) => (
                        <Tooltip
                          key={`point-${point.item[xKey]}-${pointIndex}`}
                          title={
                            <div>
                              <div style={{ marginBottom: 4, color: '#9ca3af', fontSize: 12 }}>
                                {String(point.item[xKey])}
                              </div>
                              <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
                                {String(key).replace(/_/g, " ").charAt(0).toUpperCase() + String(key).replace(/_/g, " ").slice(1)}
                              </div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>
                                {formatValue(point.value, false)}
                              </div>
                            </div>
                          }
                          placement="top"
                          overlayClassName="custom-chart-tooltip"
                        >
                          <circle
                            cx={point.x}
                            cy={point.y}
                            r="4"
                            fill={lineColor}
                            className="cursor-pointer"
                            style={{
                              opacity: legendOpacity,
                              transition: enableTransitions ? 'opacity 0.2s ease-out' : 'none'
                            }}
                          />
                        </Tooltip>
                      ))}
                    </g>
                  )
                })}
              </g>
            )}
          </svg>
        </div>

        {/* Sticky Right Y-axis section when scrollable and dual-axis */}
        {needsScroll && useDualAxis && (
          <div className="flex-shrink-0 sticky right-0 z-10 bg-white" style={{ width: margins.right }}>
            <svg width={margins.right} height={height}>
              {/* Right Y-axis line */}
              <line
                x1={1}
                y1={margins.top}
                x2={1}
                y2={margins.top + chartHeight}
                stroke="#D5D7DA"
                strokeWidth="2"
              />
              {/* Right Y-axis ticks and labels */}
              {yTicksRight.map((tick, i) => {
                const y = margins.top + chartHeight - (tick / maxLowValue) * chartHeight
                return (
                  <text
                    key={i}
                    x={12}
                    y={y + 5}
                    textAnchor="start"
                    className="fill-muted-foreground text-xs"
                  >
                    {formatValue(tick)}
                  </text>
                )
              })}
            </svg>
          </div>
        )}
      </div>

      {/* X-axis label (outside scrollable area) */}
      {(xAxisLabel || autoXAxisLabel) && (
        <div className="text-center mt-2">
          <span className="text-sm font-medium text-foreground">
            {xAxisLabel || autoXAxisLabel}
          </span>
        </div>
      )}

      {/* Brush component for scrollable charts */}
      {needsScroll && (() => {
        const brushWidth = brushContainerWidth || dimensions.width - 4
        const selectionWidth = (brushSelection.end - brushSelection.start) * brushWidth
        const selectionX = brushSelection.start * brushWidth

        const handleBrushMouseDown = (e: React.MouseEvent, type: 'start' | 'end' | 'middle') => {
          e.preventDefault()
          setIsDraggingBrush(type)
        }

        return (
          <div className="mt-4 px-2">
            <div ref={brushContainerRef} className="relative bg-gray-50 rounded border border-gray-200 overflow-hidden">
              <svg width="100%" height={60} style={{ userSelect: 'none', display: 'block' }}>
                {/* Miniature bars OR lines for high value keys */}
                {!forceHighValueKeysAsLines ? (
                  <g opacity={0.4}>
                    {/* Bars */}
                    {data.map((item, index) => {
                      const x = (index / (data.length - 1 || 1)) * brushWidth
                      const barW = Math.max(2, brushWidth / data.length - 1)

                      return highValueKeys.map((key, keyIndex) => {
                        const value = typeof item[key] === 'number' ? item[key] as number : 0
                        const normalizedHeight = (value / maxHighValue) * 50
                        const barH = Math.max(1, normalizedHeight)
                        const y = 55 - barH
                        const color = multiBar ? colors[keyIndex % colors.length] : fill

                        return (
                          <rect
                            key={`${index}-${keyIndex}`}
                            x={x - barW / 2}
                            y={y}
                            width={barW}
                            height={barH}
                            fill={color}
                          />
                        )
                      })
                    })}
                  </g>
                ) : (
                  <g opacity={0.6}>
                    {/* Lines for high value keys when forceHighValueKeysAsLines is true */}
                    {highValueKeys.map((key, keyIndex) => {
                      const lineColor = colors[keyIndex % colors.length]
                      const points = data.map((item, i) => {
                        const x = (i / (data.length - 1 || 1)) * brushWidth
                        const value = typeof item[key] === 'number' ? item[key] as number : 0
                        const normalizedHeight = (value / maxHighValue) * 50
                        const y = 55 - normalizedHeight
                        return `${x},${y}`
                      }).join(' ')

                      return (
                        <polyline
                          key={`line-high-${keyIndex}`}
                          points={points}
                          fill="none"
                          stroke={lineColor}
                          strokeWidth={1.5}
                        />
                      )
                    })}
                  </g>
                )}

                {/* Miniature lines for low value keys (dual axis) */}
                {useDualAxis && lowValueKeys.length > 0 && (
                  <g opacity={0.6}>
                    {lowValueKeys.map((key, keyIndex) => {
                      const lineColor = colors[(highValueKeys.length + keyIndex) % colors.length]
                      const points = data.map((item, i) => {
                        const x = (i / (data.length - 1 || 1)) * brushWidth
                        const value = typeof item[key] === 'number' ? item[key] as number : 0
                        const normalizedHeight = (value / maxLowValue) * 50
                        const y = 55 - normalizedHeight
                        return `${x},${y}`
                      }).join(' ')

                      return (
                        <polyline
                          key={`line-low-${keyIndex}`}
                          points={points}
                          fill="none"
                          stroke={lineColor}
                          strokeWidth={1.5}
                        />
                      )
                    })}
                  </g>
                )}

                {/* Outside selection areas (dimmed) */}
                <rect x={0} y={0} width={selectionX} height={60} fill="rgba(200, 200, 200, 0.5)" />
                <rect x={selectionX + selectionWidth} y={0} width={brushWidth - (selectionX + selectionWidth)} height={60} fill="rgba(200, 200, 200, 0.5)" />

                {/* Selection window */}
                <rect
                  x={selectionX}
                  y={0}
                  width={selectionWidth}
                  height={60}
                  fill="rgba(96, 159, 129, 0.15)"
                  stroke="rgba(96, 159, 129, 0.8)"
                  strokeWidth={2}
                  style={{ cursor: 'move' }}
                  onMouseDown={(e) => handleBrushMouseDown(e, 'middle')}
                />

                {/* Left handle */}
                <rect
                  x={selectionX - 4}
                  y={0}
                  width={8}
                  height={60}
                  fill="rgba(96, 159, 129, 0.9)"
                  style={{ cursor: 'ew-resize' }}
                  onMouseDown={(e) => handleBrushMouseDown(e, 'start')}
                />

                {/* Right handle */}
                <rect
                  x={selectionX + selectionWidth - 4}
                  y={0}
                  width={8}
                  height={60}
                  fill="rgba(96, 159, 129, 0.9)"
                  style={{ cursor: 'ew-resize' }}
                  onMouseDown={(e) => handleBrushMouseDown(e, 'end')}
                />
              </svg>
            </div>
          </div>
        )
      })()}

      {showLegend && multiBar && (
        <div className="flex flex-wrap justify-center sm:justify-end gap-3 sm:gap-4 mt-4 px-2">
          {(useDualAxis ? [...highValueKeys, ...lowValueKeys] : yKeys).map((key, i) => {
            const isHighlighted = hoveredLegend === null || hoveredLegend === key
            const opacity = hoveredLegend && hoveredLegend !== key ? 0.4 : 1
            const isLowValueKey = useDualAxis && lowValueKeys.includes(key)

            return (
              <div
                key={key}
                className="flex items-center gap-1.5 cursor-pointer transition-opacity duration-300"
                onMouseEnter={() => setHoveredLegend(key)}
                onMouseLeave={() => setHoveredLegend(null)}
                style={{ opacity }}
              >
                <div
                  className={`${isLowValueKey ? 'w-3 h-0.5' : 'w-2 h-3.5'} rounded-lg transition-all duration-300 flex-shrink-0`}
                  style={{
                    backgroundColor: colors[i % colors.length],
                    transform: isHighlighted && hoveredLegend ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
                <span className="text-xs sm:text-sm text-muted-foreground capitalize whitespace-nowrap">
                  {String(key).replace(/_/g, " ")}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface CustomMultiBarChartProps extends Omit<CustomBarChartProps, "multiBar"> {
  // dataKey is optional for multi-bar charts as it auto-detects all numeric columns
}

export const CustomMultiBarChart: React.FC<CustomMultiBarChartProps> = (props) => {
  return <CustomBarChart {...props} multiBar={true} />
}

interface CustomMultiBarChartWithLineProps extends Omit<CustomBarChartProps, "multiBar" | "enableDualAxis" | "dualAxisThreshold" | "forceLowValueKeys"> {
  // This component displays all keys except the last as bars,
  // and the last key as a line on a secondary y-axis
}

export const CustomMultiBarChartWithLine: React.FC<CustomMultiBarChartWithLineProps> = ({
  data,
  dataKey,
  ...otherProps
}) => {
  // Extract the last numeric key from the data to use as the line chart
  const lastKey = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return undefined

    const firstItem = data[0]
    if (!firstItem || typeof firstItem !== 'object') return undefined

    // Get all keys that have numeric values
    const numericKeys = Object.keys(firstItem).filter(key => {
      const value = firstItem[key]
      return typeof value === 'number' && !isNaN(value)
    })

    // Return the last numeric key
    return numericKeys.length > 0 ? numericKeys[numericKeys.length - 1] : undefined
  }, [data])

  // Force the last key to be rendered as a line on the secondary y-axis
  // All other keys will be rendered as bars on the primary y-axis
  return <CustomBarChart
    {...otherProps}
    data={data}
    dataKey={dataKey}
    multiBar={true}
    enableDualAxis={true}
    dualAxisThreshold={10}  // Threshold doesn't matter since we're using forceLowValueKeys
    forceLowValueKeys={lastKey ? [lastKey] : undefined}  // Explicitly set the last key as a line
  />
}

interface CustomBarChartWithLineProps extends Omit<CustomBarChartProps, "multiBar" | "enableDualAxis" | "dualAxisThreshold" | "forceLowValueKeys"> {
  // This component displays a single bar chart and a single line chart
  // First numeric key: bar on primary y-axis
  // Second numeric key: line on secondary y-axis
}

export const CustomBarChartWithLine: React.FC<CustomBarChartWithLineProps> = ({
  data,
  dataKey,
  ...otherProps
}) => {
  // Extract the first two numeric keys from the data
  const { firstKey, secondKey } = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { firstKey: undefined, secondKey: undefined }
    }

    const firstItem = data[0]
    if (!firstItem || typeof firstItem !== 'object') {
      return { firstKey: undefined, secondKey: undefined }
    }

    // Get all keys that have numeric values
    const numericKeys = Object.keys(firstItem).filter(key => {
      const value = firstItem[key]
      return typeof value === 'number' && !isNaN(value)
    })

    // Return first two keys (or undefined if less than 2)
    return {
      firstKey: numericKeys.length > 0 ? numericKeys[0] : undefined,
      secondKey: numericKeys.length > 1 ? numericKeys[1] : undefined
    }
  }, [data])

  // If we don't have 2 keys, fall back to regular bar chart
  if (!firstKey || !secondKey) {
    return <CustomBarChart
      {...otherProps}
      data={data}
      dataKey={dataKey}
      multiBar={false}
    />
  }

  // Force only the first key to be visible (as bar) and second key as line on secondary axis
  return <CustomBarChart
    {...otherProps}
    data={data}
    dataKey={firstKey}  // Only show first key as bar
    multiBar={false}  // Single bar, not multi-bar
    enableDualAxis={true}
    dualAxisThreshold={10}
    forceLowValueKeys={[secondKey]}  // Second key as line on secondary axis
  />
}

interface CustomMultiLineChartWithSecondaryAxisProps extends Omit<CustomBarChartProps, "multiBar" | "enableDualAxis" | "dualAxisThreshold" | "forceLowValueKeys" | "forceHighValueKeysAsLines" | "barWidth"> {
  // This component displays all keys as lines,
  // with all keys except the last on primary y-axis and the last key on secondary y-axis
}

export const CustomMultiLineChartWithSecondaryAxis: React.FC<CustomMultiLineChartWithSecondaryAxisProps> = ({
  data,
  dataKey,
  ...otherProps
}) => {
  // Extract all numeric keys from the data
  const { allKeys, lastKey, otherKeys } = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { allKeys: [], lastKey: undefined, otherKeys: [] }
    }

    const firstItem = data[0]
    if (!firstItem || typeof firstItem !== 'object') {
      return { allKeys: [], lastKey: undefined, otherKeys: [] }
    }

    // Get all keys that have numeric values
    const numericKeys = Object.keys(firstItem).filter(key => {
      const value = firstItem[key]
      return typeof value === 'number' && !isNaN(value)
    })

    if (numericKeys.length === 0) {
      return { allKeys: [], lastKey: undefined, otherKeys: [] }
    }

    // Return all keys, the last key, and all other keys
    const last = numericKeys[numericKeys.length - 1]
    const others = numericKeys.slice(0, -1)

    return {
      allKeys: numericKeys,
      lastKey: last,
      otherKeys: others
    }
  }, [data])

  // Render all keys except the last as lines on primary y-axis (using forceHighValueKeysAsLines)
  // and the last key as a line on secondary y-axis (using forceLowValueKeys)
  return <CustomBarChart
    {...otherProps}
    data={data}
    dataKey={dataKey}
    multiBar={true}
    enableDualAxis={true}
    dualAxisThreshold={10}  // Threshold doesn't matter since we're using explicit key assignment
    forceLowValueKeys={lastKey ? [lastKey] : undefined}  // Last key on secondary axis as a line
    forceHighValueKeysAsLines={true}  // Render all other keys as lines on primary axis
    barWidth={0}  // Not really needed anymore but kept for safety
  />
}

interface CustomAreaChartProps extends Omit<CustomBarChartProps, "barWidth"> {
  strokeWidth?: number
  fillOpacity?: number
  smooth?: boolean
  xAxisLabel?: string
  yAxisLabel?: string
  isResponsive?: boolean
}

export const CustomAreaChart: React.FC<CustomAreaChartProps> = ({
  data,
  dataKey,
  xAxisKey,
  className,
  height = 400,
  fill = "hsl(142, 52%, 37%)",
  strokeWidth = 2,
  fillOpacity = 0.2,
  smooth = true,
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  multiBar = false,
  xAxisLabel,
  yAxisLabel,
  isResponsive = true
}) => {
  const [hoveredPoint, setHoveredPoint] = React.useState<number | null>(null)
  const [hoveredLegend, setHoveredLegend] = React.useState<string | null>(null)
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 })
  const [error, setError] = React.useState<string | null>(null)
  const [brushSelection, setBrushSelection] = React.useState({ start: 0, end: 1 }) // Start with full data view
  const [isDraggingBrush, setIsDraggingBrush] = React.useState<'start' | 'end' | 'middle' | null>(null)
  const [brushContainerWidth, setBrushContainerWidth] = React.useState(0)
  const [enableTransitions, setEnableTransitions] = React.useState(true)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const brushContainerRef = React.useRef<HTMLDivElement>(null)

  // Generate unique ID for this chart instance to avoid gradient conflicts
  const chartId = React.useMemo(() => {
    // Use a simpler ID format that's more reliable
    return `c${Date.now()}${Math.floor(Math.random() * 1000)}`
  }, [])

  // Validate data and detect errors
  React.useEffect(() => {
    try {
      setError(null)

      // Check if data exists - don't render chart if no data
      if (!data || !Array.isArray(data)) {
        setError("no-data")
        return
      }

      if (data.length === 0) {
        setError("no-data")
        return
      }

      // Check if data has valid structure
      if (typeof data[0] !== 'object') {
        setError("Invalid data format")
        return
      }

      // Special handling for single data point - line charts need at least 2 points
      if (data.length === 1) {
        setError("Line chart requires at least 2 data points. Consider using a bar chart for single values.")
        return
      }

      // Check for numeric values
      const hasNumericData = data.some(item =>
        item && typeof item === 'object' && Object.values(item).some(value => typeof value === 'number' && !isNaN(value))
      )

      if (!hasNumericData) {
        setError("No numeric data found to plot")
        return
      }

      // Check for invalid values
      const hasInvalidValues = data.some(item =>
        item && typeof item === 'object' && Object.values(item).some(value =>
          typeof value === 'number' && (isNaN(value) || !isFinite(value))
        )
      )

      if (hasInvalidValues) {
        setError("Data contains invalid numeric values (NaN or Infinity)")
        return
      }

    } catch (err) {
      setError(`Error processing data: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }, [data])

  // Auto-detect keys
  const detectKeys = React.useMemo(() => {
    if (!data || data.length === 0 || !data[0] || typeof data[0] !== 'object') return { xKey: "", yKeys: [] }

    // Collect all unique keys from ALL data items, not just the first one
    // This handles cases where some items might be missing certain keys
    const allKeysSet = new Set<string>()
    const numericKeysSet = new Set<string>()
    const nonNumericKeysSet = new Set<string>()

    data.forEach(item => {
      if (item && typeof item === 'object') {
        Object.keys(item).forEach(key => {
          allKeysSet.add(key)
          if (typeof item[key] === 'number') {
            numericKeysSet.add(key)
          } else {
            nonNumericKeysSet.add(key)
          }
        })
      }
    })

    const numericKeys = Array.from(numericKeysSet)
    const nonNumericKeys = Array.from(nonNumericKeysSet)
    const allKeys = Array.from(allKeysSet)

    return {
      xKey: xAxisKey || nonNumericKeys[0] || allKeys[0],
      yKeys: multiBar ? numericKeys : [dataKey || numericKeys[0]]
    }
  }, [data, xAxisKey, dataKey, multiBar])

  const { xKey, yKeys } = detectKeys

  // Debug logging
  React.useEffect(() => {
    console.log('[CustomAreaChart] Detected yKeys:', yKeys)
    console.log('[CustomAreaChart] multiBar:', multiBar)
    console.log('[CustomAreaChart] dataKey:', dataKey)
    console.log('[CustomAreaChart] First data item keys:', data[0] ? Object.keys(data[0]) : 'no data')
  }, [yKeys, multiBar, dataKey, data])

  // Auto-generate Y-axis label if not provided
  const autoYAxisLabel = React.useMemo(() => {
    if (yAxisLabel) return yAxisLabel
    if (!yKeys || yKeys.length === 0) return ""

    // Don't show y-axis label for multi-bar charts
    if (multiBar) return ""

    // If single key, use it as label
    if (yKeys.length === 1) {
      return yKeys[0]
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    }

    return ""
  }, [yAxisLabel, yKeys, multiBar])

  // Auto-generate X-axis label if not provided
  const autoXAxisLabel = React.useMemo(() => {
    if (xAxisLabel) return xAxisLabel
    if (!xKey) return ""

    // Check if x-axis data appears to be dates
    if (data && data.length > 0) {
      const firstValue = String(data[0][xKey])
      // Check for common date patterns including date ranges
      const isDate = firstValue.match(/^\d{4}-\d{2}-\d{2}/) ||
        firstValue.match(/^\d{2}\/\d{2}\/\d{4}/) ||
        firstValue.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i) ||
        firstValue.match(/^\d{4}-\d{2}-\d{2}\s+to\s+\d{4}-\d{2}-\d{2}$/)

      // Don't show label for date axes
      if (isDate) return ""
    }

    return xKey
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  }, [xAxisLabel, xKey, data])

  // 🔥 ResizeObserver for responsiveness
  React.useEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setDimensions({
            width: entry.contentRect.width,
            height,
          })
        }
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [height])

  const margins = { top: 20, right: 20, bottom: (xAxisLabel || autoXAxisLabel) ? 100 : 80, left: (yAxisLabel || autoYAxisLabel) ? 80 : 60 }

  // Calculate minimum width needed based on data points
  const MIN_POINT_SPACING = 40 // Minimum spacing per data point for line/area charts
  const baseChartWidth = dimensions.width - margins.left - margins.right
  const minChartWidth = Math.max(400, data.length * MIN_POINT_SPACING)

  // Enable brush when chart width exceeds container width
  const needsScroll = minChartWidth > baseChartWidth
  const chartWidth = baseChartWidth // Use viewport width, zoom via data filtering
  const chartHeight = height - margins.top - margins.bottom

  // Filter visible data based on brush selection (zoom level)
  const visibleData = React.useMemo(() => {
    if (!needsScroll) return data

    const startIndex = Math.floor(brushSelection.start * data.length)
    const endIndex = Math.ceil(brushSelection.end * data.length)

    return data.slice(startIndex, endIndex)
  }, [data, brushSelection.start, brushSelection.end, needsScroll])

  // Brush interaction handlers
  React.useEffect(() => {
    if (!isDraggingBrush) {
      const timer = setTimeout(() => setEnableTransitions(true), 50)
      return () => clearTimeout(timer)
    }

    setEnableTransitions(false)

    const handleMouseMove = (e: MouseEvent) => {
      if (!brushContainerRef.current) return

      const rect = brushContainerRef.current.getBoundingClientRect()
      const brushWidth = rect.width
      const x = Math.max(0, Math.min(e.clientX - rect.left, brushWidth))
      const ratio = x / brushWidth

      setBrushSelection(prev => {
        if (isDraggingBrush === 'start') {
          const newStart = Math.max(0, Math.min(ratio, prev.end - 0.05))
          return { start: newStart, end: prev.end }
        } else if (isDraggingBrush === 'end') {
          const newEnd = Math.max(prev.start + 0.05, Math.min(ratio, 1))
          return { start: prev.start, end: newEnd }
        } else if (isDraggingBrush === 'middle') {
          const width = prev.end - prev.start
          let newStart = ratio - width / 2
          newStart = Math.max(0, Math.min(newStart, 1 - width))
          return { start: newStart, end: newStart + width }
        }
        return prev
      })
    }

    const handleMouseUp = () => {
      setIsDraggingBrush(null)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDraggingBrush])

  // Measure brush container width
  React.useEffect(() => {
    if (!brushContainerRef.current) return

    const updateBrushWidth = () => {
      if (brushContainerRef.current) {
        setBrushContainerWidth(brushContainerRef.current.offsetWidth)
      }
    }

    updateBrushWidth()

    const observer = new ResizeObserver(updateBrushWidth)
    observer.observe(brushContainerRef.current)

    return () => observer.disconnect()
  }, [needsScroll])

  const maxValue = React.useMemo(() => {
    let max = 0
    data.forEach((item) => {
      yKeys.forEach((key) => {
        const value = item[key] as number
        if (value > max) max = value
      })
    })
    return Math.max(Math.ceil(max * 1.1), 10)
  }, [data, yKeys])

  const getX = (index: number) => {
    const baseX = needsScroll ? 0 : margins.left
    const dataLength = needsScroll ? visibleData.length : data.length
    return baseX + (index / (dataLength - 1)) * chartWidth
  }
  const getY = (value: number) => margins.top + chartHeight - (value / maxValue) * chartHeight

  const colors = [
    "hsl(142, 52%, 37%)",  // Green
    "hsl(24, 100%, 50%)",   // Orange
    "hsl(210, 100%, 50%)",  // Blue
    "hsl(340, 82%, 52%)",   // Pink/Red
    "hsl(45, 93%, 47%)",    // Yellow/Gold
    "hsl(271, 76%, 53%)",   // Purple
    "hsl(184, 77%, 34%)",   // Teal
    "hsl(320, 70%, 40%)"    // Magenta
  ]

  const yTicks = React.useMemo(() => {
    const tickCount = 5
    return Array.from({ length: tickCount + 1 }, (_, i) => (maxValue / tickCount) * i)
  }, [maxValue])

  const xTicks = React.useMemo(() => {
    const dataToUse = needsScroll ? visibleData : data
    if (!Array.isArray(dataToUse) || dataToUse.length === 0) return []
    return dataToUse.map((_, i) => margins.left + (i / (dataToUse.length - 1)) * chartWidth)
  }, [data, visibleData, needsScroll, margins.left, chartWidth])

  const formatValue = (value: number | undefined, useAbbreviation: boolean = true) => {
    if (value === undefined || value === null) return '0'
    if (useAbbreviation) {
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
      // For values less than 1, show 2 decimal places; otherwise show appropriate decimals
      if (value < 1 && value > 0) return value.toFixed(2)
      if (value < 10) return value.toFixed(1)
      return value.toFixed(0)
    }
    // For tooltip (not abbreviated), show more precision for small values
    if (value < 1 && value > 0) return value.toFixed(2)
    if (value < 10) return value.toFixed(1)
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
  }

  const formatXLabel = (value: string | number | undefined) => {
    if (value === undefined || value === null) return ''
    const str = String(value)

    // Handle date range format "YYYY-MM-DD to YYYY-MM-DD"
    if (str.match(/^\d{4}-\d{2}-\d{2}\s+to\s+\d{4}-\d{2}-\d{2}$/)) {
      const dates = str.split(' to ')
      const startDate = new Date(dates[0])
      const endDate = new Date(dates[1])

      // Format as "Sep 17-23" if same month, or "Sep 17 - Sep 23" if different months
      const startMonth = startDate.toLocaleDateString("en-US", { month: "short" })
      const endMonth = endDate.toLocaleDateString("en-US", { month: "short" })
      const startDay = startDate.getDate()
      const endDay = endDate.getDate()

      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}`
      } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
      }
    }

    // Handle single date format
    if (str.match(/^\d{4}-\d{2}-\d{2}/)) {
      const date = new Date(str)
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    }

    return str.length > 10 ? str.substring(0, 10) + "..." : str
  }

  const createPath = (key: string, smooth: boolean = true) => {
    if (!Array.isArray(visibleData) || !smooth || visibleData.length < 2) {
      // Fallback to straight lines if smooth is disabled or insufficient data
      if (!Array.isArray(visibleData)) return ""
      return visibleData
        .map((item, i) => {
          const x = getX(i)
          const y = getY(item[key] as number)
          return `${i === 0 ? "M" : "L"} ${x} ${y}`
        })
        .join(" ")
    }

    // Create smooth curve using cubic Bézier curves
    const points = visibleData.map((item, i) => ({
      x: getX(i),
      y: getY(item[key] as number),
      value: item[key] as number
    }))

    // Generate smooth curve path using monotone cubic interpolation
    // This ensures the curve passes through all points smoothly without overshooting
    let path = `M ${points[0].x} ${points[0].y}`

    const maxY = margins.top + chartHeight // This is the y position for value 0

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = i > 0 ? points[i - 1] : points[i]
      const p1 = points[i]
      const p2 = points[i + 1]
      const p3 = i < points.length - 2 ? points[i + 2] : points[i + 1]

      // Calculate tangents using cardinal splines with tension
      const tension = 0.5 // Balanced tension for smooth, visible curves
      const t1x = tension * (p2.x - p0.x)
      const t1y = tension * (p2.y - p0.y)
      const t2x = tension * (p3.x - p1.x)
      const t2y = tension * (p3.y - p1.y)

      // Control points
      let cp1x = p1.x + t1x / 3
      let cp1y = p1.y + t1y / 3
      let cp2x = p2.x - t2x / 3
      let cp2y = p2.y - t2y / 3

      // Clamp control points to prevent curves from going below x-axis (maxY is the 0 line in SVG)
      cp1y = Math.min(cp1y, maxY)
      cp2y = Math.min(cp2y, maxY)

      // Also prevent control points from going too high above the data points
      const minY = Math.min(p1.y, p2.y) - 20 // Allow small overshoot for smoothness
      cp1y = Math.max(cp1y, minY)
      cp2y = Math.max(cp2y, minY)

      // Ensure endpoints don't go below zero
      const clampedP2y = Math.min(p2.y, maxY)

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${clampedP2y}`
    }

    return path
  }

  const createAreaPath = (key: string) => {
    const linePath = createPath(key, smooth)
    const lastX = getX(visibleData.length - 1)
    const firstX = getX(0)
    return `${linePath} L ${lastX} ${margins.top + chartHeight} L ${firstX} ${margins.top + chartHeight
      } Z`
  }

  // Don't render anything if there's an error (except for specific environments)
  if (error) {
    // Only show error messages when using the gaggle.online base URL (development/staging)
    const isDevelopment = window.location.hostname.includes('localhost') ||
                          window.location.hostname.includes('gaggle.online')

    if (isDevelopment && error !== 'no-data') {
      return (
        <div ref={containerRef} className={cn("w-full bg-red-50 border border-red-200 rounded-lg p-4", className)} style={{ minHeight: height }}>
          <div className="flex items-center justify-center flex-col gap-2" style={{ height: height }}>
            <div className="text-red-600 font-semibold">Chart Error</div>
            <div className="text-red-500 text-sm">{error}</div>
            <div className="text-gray-500 text-xs mt-2">This error is only visible in development mode</div>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div ref={containerRef} className={cn("w-full bg-white", className)}>
      <div className="relative flex bg-white">
        {/* Sticky Left Y-axis section when scrollable */}
        {needsScroll && (
          <div className="flex-shrink-0 sticky left-0 z-10 bg-white" style={{ width: margins.left }}>
            <svg width={margins.left} height={height}>
              {/* Y-axis line */}
              <line
                x1={margins.left - 1}
                y1={margins.top}
                x2={margins.left - 1}
                y2={margins.top + chartHeight}
                stroke="#D5D7DA"
                strokeWidth="2"
              />
              {/* Y-axis ticks and labels */}
              {yTicks.map((tick, i) => (
                <text
                  key={i}
                  x={margins.left - 12}
                  y={margins.top + chartHeight - (tick / maxValue) * chartHeight + 5}
                  textAnchor="end"
                  className="fill-muted-foreground text-xs"
                >
                  {formatValue(tick)}
                </text>
              ))}
              {/* Y-axis label */}
              {(yAxisLabel || autoYAxisLabel) && (
                <text
                  x={-(margins.top + chartHeight / 2)}
                  y={15}
                  transform="rotate(-90)"
                  textAnchor="middle"
                  className="fill-foreground text-sm font-medium"
                >
                  {yAxisLabel || autoYAxisLabel}
                </text>
              )}
            </svg>
          </div>
        )}

        {/* Scrollable chart area */}
        <div className={cn("relative flex-1", needsScroll && "overflow-x-auto overflow-y-visible")}>
          <svg width={needsScroll ? chartWidth : dimensions.width} height={height} className="overflow-visible" style={{ backgroundColor: 'white' }}>
            {/* White background rect to ensure no transparency issues */}
            <rect x={0} y={0} width={needsScroll ? chartWidth : dimensions.width} height={height || 400} fill="white" stroke="none" />

            {/* Gradient definitions */}
            <defs>
              {yKeys.map((key, i) => {
                const gradientColor = multiBar ? colors[i % colors.length] : fill
                const gradientId = `gradient-${chartId}-${key}`
                return (
                  <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={gradientColor} stopOpacity={0.02} />
                    <stop offset="50%" stopColor={gradientColor} stopOpacity={0.01} />
                    <stop offset="100%" stopColor={gradientColor} stopOpacity={0.001} />
                  </linearGradient>
                )
              })}
            </defs>

            {/* Horizontal grid lines */}
            {showGrid && (
              <g>
                {yTicks.map((tick, i) => {
                  const startX = needsScroll ? 0 : margins.left
                  return (
                    <line
                      key={`h-${i}`}
                      x1={startX}
                      y1={margins.top + chartHeight - (tick / maxValue) * chartHeight}
                      x2={startX + chartWidth}
                      y2={margins.top + chartHeight - (tick / maxValue) * chartHeight}
                      stroke="#D5D7DA"
                      strokeWidth="1"
                      opacity="0.3"
                    />
                  )
                })}
              </g>
            )}

            {/* Vertical grid lines - removed for cleaner look */}

            {/* Y-axis (only show when not scrollable, otherwise it's in sticky section) */}
            {!needsScroll && (
              <g>
                <line
                  x1={margins.left}
                  y1={margins.top}
                  x2={margins.left}
                  y2={margins.top + chartHeight}
                  stroke="#D5D7DA"
                  strokeWidth="2"
                />
                {yTicks.map((tick, i) => (
                  <text
                    key={i}
                    x={margins.left - 10}
                    y={margins.top + chartHeight - (tick / maxValue) * chartHeight + 5}
                    textAnchor="end"
                    className="fill-muted-foreground text-xs"
                  >
                    {formatValue(tick)}
                  </text>
                ))}
                {/* Y-axis label */}
                {(yAxisLabel || autoYAxisLabel) && (
                  <text
                    x={15}
                    y={margins.top + chartHeight / 2}
                    transform={`rotate(-90, 15, ${margins.top + chartHeight / 2})`}
                    textAnchor="middle"
                    className="fill-foreground text-sm font-medium"
                  >
                    {yAxisLabel || autoYAxisLabel}
                  </text>
                )}
              </g>
            )}

            {/* X-axis */}
            <g>
              <line
                x1={needsScroll ? 0 : margins.left}
                y1={margins.top + chartHeight}
                x2={(needsScroll ? 0 : margins.left) + chartWidth}
                y2={margins.top + chartHeight}
                stroke="#D5D7DA"
                strokeWidth="2"
              />
              {Array.isArray(visibleData) && visibleData.map((item, i) => {
                const x = getX(i)
                const y = margins.top + chartHeight + 20
                // Show every label on wide screens with few points, otherwise show fewer labels
                const shouldShow = visibleData.length <= 10 || dimensions.width > 800 || i % Math.ceil(visibleData.length / 10) === 0
                return shouldShow ? (
                  <text
                    key={i}
                    transform={`translate(${x}, ${y}) rotate(-45)`}
                    textAnchor="end"
                    className="fill-muted-foreground text-xs"
                  >
                    {formatXLabel(item[xKey])}
                  </text>
                ) : null
              })}
            </g>

            {/* Areas and lines */}
            {yKeys.map((key, i) => {
              const color = multiBar ? colors[i % colors.length] : fill
              const gradientId = `gradient-${chartId}-${key}`
              const useGradient = fillOpacity > 0
              const isHighlighted = hoveredLegend === null || hoveredLegend === key
              const opacity = hoveredLegend && hoveredLegend !== key ? 0.2 : 1

              const linePath = createPath(key, smooth)
              const areaPath = createAreaPath(key)

              // Debug logging for path generation
              if (i < 3) { // Only log first 3 to avoid spam
                console.log(`[CustomAreaChart] Rendering line ${i} for key "${key}":`, {
                  color,
                  linePathLength: linePath.length,
                  areaPathLength: areaPath.length,
                  opacity,
                  visibleDataLength: visibleData.length
                })
              }

              return (
                <g key={key}>
                  <path
                    d={areaPath}
                    fill={color}
                    fillOpacity={0.08}
                    opacity={opacity}
                    style={{
                      transition: 'opacity 0.3s ease',
                      pointerEvents: 'none'
                    }}
                  />
                  <path
                    d={linePath}
                    fill="none"
                    stroke={color}
                    strokeWidth={isHighlighted ? strokeWidth + 1 : strokeWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity={opacity}
                    style={{ transition: 'all 0.3s ease' }}
                  />
                </g>
              )
            })}

            {/* Points with combined tooltip */}
            {showTooltip && Array.isArray(visibleData) &&
              visibleData.map((item, dataIndex) => {
                const x = getX(dataIndex)

                return (
                  <g key={`tooltip-group-${dataIndex}`}>
                    {/* Invisible larger area for easier hovering */}
                    <rect
                      x={x - 15}
                      y={margins.top}
                      width={30}
                      height={chartHeight}
                      fill="transparent"
                      onMouseEnter={() => setHoveredPoint(dataIndex)}
                      onMouseLeave={() => setHoveredPoint(null)}
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                    />

                    {/* Single combined tooltip for all lines at this x position */}
                    <Tooltip
                      title={
                        <div style={{ minWidth: 150 }}>
                          <div style={{ marginBottom: 8, color: '#9ca3af', fontSize: 12 }}>
                            {(() => {
                              const xLabelFull = String(item[xKey])
                              return xLabelFull.match(/^\d{4}-\d{2}-\d{2}/)
                                ? new Date(xLabelFull).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                : xLabelFull
                            })()}
                          </div>
                          {yKeys.map((key, keyIndex) => {
                            const value = item[key] as number
                            const color = multiBar ? colors[keyIndex % colors.length] : fill
                            return (
                              <div key={key} style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }}></div>
                                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontWeight: 600, fontSize: 12, color: '#374151' }}>
                                    {String(key).replace(/_/g, " ").charAt(0).toUpperCase() + String(key).replace(/_/g, " ").slice(1)}
                                  </span>
                                  <span style={{ fontWeight: 700, fontSize: 13, color: color }}>
                                    {formatValue(value, false)}
                                  </span>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      }
                      placement="top"
                      overlayClassName="custom-chart-tooltip"
                      visible={hoveredPoint === dataIndex}
                    >
                      <g>
                        {yKeys.map((key, keyIndex) => {
                          const value = item[key] as number
                          const y = getY(value)
                          const color = multiBar ? colors[keyIndex % colors.length] : fill
                          const legendOpacity = hoveredLegend && hoveredLegend !== key ? 0.2 : 1

                          return (
                            <circle
                              key={`${dataIndex}-${keyIndex}`}
                              cx={x}
                              cy={y}
                              r={hoveredPoint === dataIndex ? 5 : 0}
                              fill="white"
                              stroke={color}
                              strokeWidth={hoveredPoint === dataIndex ? 2 : 0}
                              className="transition-all"
                              style={{
                                transition: 'all 0.2s ease-in-out',
                                opacity: hoveredPoint === dataIndex ? legendOpacity : 0
                              }}
                            />
                          )
                        })}
                      </g>
                    </Tooltip>
                  </g>
                )
              })
            }
          </svg>
        </div>
      </div>

      {/* X-axis label (outside scrollable area) */}
      {(xAxisLabel || autoXAxisLabel) && (
        <div className="text-center mt-2">
          <span className="text-sm font-medium text-foreground">
            {xAxisLabel || autoXAxisLabel}
          </span>
        </div>
      )}

      {/* Brush component for scrollable charts */}
      {needsScroll && (() => {
        const brushWidth = brushContainerWidth || dimensions.width - 4
        const selectionWidth = (brushSelection.end - brushSelection.start) * brushWidth
        const selectionX = brushSelection.start * brushWidth

        const handleBrushMouseDown = (e: React.MouseEvent, type: 'start' | 'end' | 'middle') => {
          e.preventDefault()
          setIsDraggingBrush(type)
        }

        // Create smooth curve for brush preview
        const createBrushPath = (key: string) => {
          const points = data.map((item, i) => ({
            x: (i / (data.length - 1 || 1)) * brushWidth,
            y: 55 - (typeof item[key] === 'number' ? (item[key] as number) / maxValue : 0) * 50
          }))

          if (points.length < 2) return ''

          let path = `M ${points[0].x} ${points[0].y}`

          for (let i = 0; i < points.length - 1; i++) {
            const p0 = i > 0 ? points[i - 1] : points[i]
            const p1 = points[i]
            const p2 = points[i + 1]
            const p3 = i < points.length - 2 ? points[i + 2] : points[i + 1]

            const tension = 0.5
            const t1x = tension * (p2.x - p0.x)
            const t1y = tension * (p2.y - p0.y)
            const t2x = tension * (p3.x - p1.x)
            const t2y = tension * (p3.y - p1.y)

            const cp1x = p1.x + t1x / 3
            const cp1y = p1.y + t1y / 3
            const cp2x = p2.x - t2x / 3
            const cp2y = p2.y - t2y / 3

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
          }

          return path
        }

        const createBrushAreaPath = (key: string) => {
          const linePath = createBrushPath(key)
          const lastX = (data.length - 1) / (data.length - 1 || 1) * brushWidth
          return `${linePath} L ${lastX} 55 L 0 55 Z`
        }

        return (
          <div className="mt-4 px-2">
            <div ref={brushContainerRef} className="relative bg-gray-50 rounded border border-gray-200 overflow-hidden">
              <svg width="100%" height={60} style={{ userSelect: 'none', display: 'block' }}>
                {/* Miniature area chart */}
                <defs>
                  {yKeys.map((key, keyIndex) => {
                    const color = multiBar ? colors[keyIndex % colors.length] : fill
                    return (
                      <linearGradient key={`gradient-${keyIndex}`} id={`brushGradient-area-${keyIndex}`} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.4" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.1" />
                      </linearGradient>
                    )
                  })}
                </defs>
                <g>
                  {yKeys.map((key, keyIndex) => {
                    const color = multiBar ? colors[keyIndex % colors.length] : fill
                    const linePath = createBrushPath(key)
                    const areaPath = createBrushAreaPath(key)

                    return (
                      <g key={keyIndex}>
                        <path
                          d={areaPath}
                          fill={`url(#brushGradient-area-${keyIndex})`}
                          opacity={0.6}
                        />
                        <path
                          d={linePath}
                          fill="none"
                          stroke={color}
                          strokeWidth={1.5}
                          opacity={0.8}
                        />
                      </g>
                    )
                  })}
                </g>

                {/* Outside selection areas (dimmed) */}
                <rect x={0} y={0} width={selectionX} height={60} fill="rgba(200, 200, 200, 0.5)" />
                <rect x={selectionX + selectionWidth} y={0} width={brushWidth - (selectionX + selectionWidth)} height={60} fill="rgba(200, 200, 200, 0.5)" />

                {/* Selection window */}
                <rect
                  x={selectionX}
                  y={0}
                  width={selectionWidth}
                  height={60}
                  fill="rgba(96, 159, 129, 0.15)"
                  stroke="rgba(96, 159, 129, 0.8)"
                  strokeWidth={2}
                  style={{ cursor: 'move' }}
                  onMouseDown={(e) => handleBrushMouseDown(e, 'middle')}
                />

                {/* Left handle */}
                <rect
                  x={selectionX - 4}
                  y={0}
                  width={8}
                  height={60}
                  fill="rgba(96, 159, 129, 0.9)"
                  style={{ cursor: 'ew-resize' }}
                  onMouseDown={(e) => handleBrushMouseDown(e, 'start')}
                />

                {/* Right handle */}
                <rect
                  x={selectionX + selectionWidth - 4}
                  y={0}
                  width={8}
                  height={60}
                  fill="rgba(96, 159, 129, 0.9)"
                  style={{ cursor: 'ew-resize' }}
                  onMouseDown={(e) => handleBrushMouseDown(e, 'end')}
                />
              </svg>
            </div>
          </div>
        )
      })()}

      {showLegend && multiBar && (
        <div className="flex flex-wrap justify-end gap-4 mt-4">
          {yKeys.map((key, i) => {
            const isHighlighted = hoveredLegend === null || hoveredLegend === key
            const opacity = hoveredLegend && hoveredLegend !== key ? 0.4 : 1

            return (
              <div
                key={key}
                className="flex items-center gap-1 cursor-pointer transition-opacity duration-300"
                onMouseEnter={() => setHoveredLegend(key)}
                onMouseLeave={() => setHoveredLegend(null)}
                style={{ opacity }}
              >
                <div
                  className="w-2 h-3.5 rounded-lg transition-all duration-300"
                  style={{
                    backgroundColor: colors[i % colors.length],
                    transform: isHighlighted && hoveredLegend ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
                <span className="text-sm text-muted-foreground capitalize">
                  {String(key).replace(/_/g, " ")}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Export line chart as a variant of area chart with gradient fill
export const CustomLineChart: React.FC<CustomAreaChartProps> = (props) => {
  return <CustomAreaChart {...props} fillOpacity={props.fillOpacity ?? 0.25} smooth={true} />
}

interface CustomPieChartProps {
  data: DataPoint[]
  dataKey?: string
  labelKey?: string
  className?: string
  size?: number
  innerRadius?: number
  showTooltip?: boolean
  showLegend?: boolean
  showLabels?: boolean
}

export const CustomPieChart: React.FC<CustomPieChartProps> = ({
  data,
  dataKey,
  labelKey,
  className,
  size = 280,
  innerRadius = 0,
  showTooltip = true,
  showLegend = true,
  showLabels = false
}) => {
  // State and Refs with explicit types
  const [hoveredSlice, setHoveredSlice] = React.useState<number | null>(null)
  const [dimensions, setDimensions] = React.useState<{ width: number, height: number }>({ width: 0, height: 0 })
  const [legendBelow, setLegendBelow] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const legendRef = React.useRef<HTMLDivElement>(null)

  // FIX: Explicitly define the return type and assert the keys as strings
  const detectKeys = React.useMemo((): { labelKey: string, valueKey: string } => {
    if (!data || data.length === 0 || typeof data[0] !== 'object' || data[0] === null) {
      return { labelKey: "", valueKey: "" }
    }

    const firstItem = data[0]
    // Note: Object.keys returns string[], we cast it for type safety within the DataPoint context
    const keys = Object.keys(firstItem) as (keyof DataPoint)[] 

    // Filter keys based on type of the value they hold
    const numericKeys = keys.filter((key) => typeof firstItem[key] === "number")
    const nonNumericKeys = keys.filter((key) => typeof firstItem[key] !== "number")

    // Use type assertion (as string) to confirm the key names are strings,
    // which resolves the "string | number" assignment conflict with the explicit return type.
    const result = {
      labelKey: (labelKey || nonNumericKeys[0] || keys[0] || "") as string,
      valueKey: (dataKey || numericKeys[0] || "") as string
    }

    return result
  }, [data, dataKey, labelKey])

  const { labelKey: detectedLabelKey, valueKey } = detectKeys

  React.useEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          const containerWidth = entry.contentRect.width
          const minChartWidth = 450
          const effectiveWidth = Math.max(containerWidth, minChartWidth)
          const maxSize = Math.min(size, effectiveWidth)
          const minDimension = Math.min(effectiveWidth, maxSize)

          setDimensions({
            width: effectiveWidth,
            height: minDimension
          })

          const minLegendWidth = 200
          const actualChartSize = minDimension
          const gapSize = 16
          const totalWidthNeeded = actualChartSize + minLegendWidth + gapSize

          const shouldBeLegendBelow = containerWidth < totalWidthNeeded
          setLegendBelow(shouldBeLegendBelow)
        }
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [size])

  const colors: string[] = [
    "#1f7a50", // Green
    "#ff6b35", // Orange
    "#4a90e2", // Blue
    "#e74c3c", // Red
    "#9b59b6", // Purple
    "#f39c12", // Yellow/Orange
    "#16a085", // Teal
    "#e67e22", // Dark Orange
    "#3498db", // Light Blue
    "#95a5a6", // Gray
    "#34495e", // Dark Blue Gray
    "#27ae60", // Emerald
    "#e91e63", // Pink
    "#00bcd4", // Cyan
    "#ff9800", // Amber
    "#607d8b"  // Blue Gray
  ]

  const total = React.useMemo((): number => {
    if (!data || !valueKey || !Array.isArray(data)) return 0
    return data.reduce((sum, item) => {
      // Type casting to DataPoint for access
      const value = (item as DataPoint)[valueKey]
      const numValue = typeof value === 'number' ? value : Number(value)
      return sum + (isNaN(numValue) ? 0 : numValue)
    }, 0)
  }, [data, valueKey])

  // Define an internal type alias for slices based on DataPoint
  type SliceType = DataPoint & {
    value: any;
    numValue: number;
    isNumeric: boolean;
    percentage: number;
    startAngle: number;
    endAngle: number;
    color: string;
    label: string;
  }

  const slices = React.useMemo((): SliceType[] => {
    if (!data || !valueKey || !Array.isArray(data)) return []

    let currentAngle = -90
    return data.map((item, index) => {
      const value = (item as DataPoint)[valueKey]
      const numValue = typeof value === 'number' ? value : Number(value)
      const isNumeric = !isNaN(numValue) && numValue > 0
      const percentage = isNumeric && total > 0 ? (numValue / total) * 100 : 0
      const angle = isNumeric && total > 0 ? (numValue / total) * 360 : 0
      const startAngle = currentAngle
      const endAngle = currentAngle + angle
      currentAngle = endAngle

      return {
        ...item,
        value,
        numValue: isNumeric ? numValue : 0,
        isNumeric,
        percentage,
        startAngle,
        endAngle,
        color: colors[index % colors.length],
        label: (item[detectedLabelKey] as string) || `Item ${index + 1}`
      } as SliceType
    })
  }, [data, valueKey, total, detectedLabelKey])

  const createPath = (startAngle: number, endAngle: number, outerRadius: number, innerRadius: number): string => {
    const centerX = dimensions.height / 2
    const centerY = dimensions.height / 2

    const startAngleRad = (startAngle * Math.PI) / 180
    const endAngleRad = (endAngle * Math.PI) / 180

    const x1 = centerX + outerRadius * Math.cos(startAngleRad)
    const y1 = centerY + outerRadius * Math.sin(startAngleRad)
    const x2 = centerX + outerRadius * Math.cos(endAngleRad)
    const y2 = centerY + outerRadius * Math.sin(endAngleRad)

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0

    if (innerRadius > 0) {
      const x3 = centerX + innerRadius * Math.cos(endAngleRad)
      const y3 = centerY + innerRadius * Math.sin(endAngleRad)
      const x4 = centerX + innerRadius * Math.cos(startAngleRad)
      const y4 = centerY + innerRadius * Math.sin(startAngleRad)

      return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4} Z`
    }

    return `M ${centerX} ${centerY} L ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`
  }

  const formatValue = (value: number | string): string => {
    const numValue = Number(value)
    if (isNaN(numValue)) return String(value)
    if (numValue >= 1000000) return `${(numValue / 1000000).toFixed(1)}M`
    if (numValue >= 1000) return `${(numValue / 1000).toFixed(1)}K`
    return numValue.toFixed(0)
  }

  const radius = Math.min(dimensions.height, dimensions.width) / 2 - 20
  const actualInnerRadius = (innerRadius / 100) * radius

  // Error Handling: No valid data points - don't render
  if (!slices || slices.length === 0) {
    return null
  }

  // Error Handling: All values are non-numeric
  const hasNumericData = slices.some(s => s.isNumeric)
  const hasNonNumericData = slices.some(s => !s.isNumeric)

  if (!hasNumericData) {
    return (
      <div ref={containerRef} className={cn("w-full", className)} style={{ minHeight: size }}>
        <div className="flex items-center justify-center" style={{ height: size }}>
          <div className="text-center">
            <svg
              className="mx-auto mb-4 text-red-400"
              width="48"
              height="48"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-red-500 font-medium">Unable to render chart</p>
            <p className="text-gray-500 text-sm mt-1">All values must be numeric</p>
          </div>
        </div>
      </div>
    )
  }

  const needsScroll = dimensions.width > (containerRef.current?.offsetWidth ?? 0)

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      {hasNonNumericData && (
        <div className="text-amber-600 text-xs mb-2 px-2 py-1 bg-amber-50 rounded border border-amber-200">
          ⚠️ Some values are not numeric and will only appear in the legend
        </div>
      )}
      <div className={cn(
        needsScroll && "overflow-x-auto",
        "relative"
      )}>
        {needsScroll && (
          <div
            className="sticky right-0 top-0 bottom-0 w-20 pointer-events-none z-50 float-right h-full"
            style={{
              background: 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 20%, rgba(255,255,255,0) 100%)',
              position: 'sticky',
              marginLeft: '-80px'
            }}
          />
        )}
        <div className={cn(
          "flex gap-2 sm:gap-4 items-start",
          legendBelow ? "flex-col" : "flex-col sm:flex-row sm:items-center items-center"
        )}>
          <svg width={dimensions.height} height={dimensions.height} className="flex-shrink-0 mx-auto">
            {slices.filter(s => s.isNumeric).map((slice, index) => {
              const path = createPath(
                slice.startAngle,
                slice.endAngle,
                hoveredSlice === index ? radius + 5 : radius,
                actualInnerRadius
              )

              return (
                <g key={index}>
                  {showTooltip ? (
                    <Tooltip
                      title={
                        <div>
                          <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
                            {String(slice.label)}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>
                            {formatValue(slice.value)}
                          </div>
                          <div style={{ color: '#9ca3af', fontSize: 12 }}>
                            {slice.percentage.toFixed(1)}%
                          </div>
                        </div>
                      }
                      placement="top"
                    >
                      <path
                        d={path}
                        fill={slice.color}
                        className="transition-all cursor-pointer"
                        opacity={hoveredSlice === null || hoveredSlice === index ? 1 : 0.7}
                        onMouseEnter={() => setHoveredSlice(index)}
                        onMouseLeave={() => setHoveredSlice(null)}
                      />
                    </Tooltip>
                  ) : (
                    <path
                      d={path}
                      fill={slice.color}
                      className="transition-all"
                      opacity={hoveredSlice === null || hoveredSlice === index ? 1 : 0.7}
                      onMouseEnter={() => setHoveredSlice(index)}
                      onMouseLeave={() => setHoveredSlice(null)}
                    />
                  )}

                  {showLabels && slice.percentage > 5 && (
                    <text
                      x={dimensions.height / 2 + (radius * 0.7) * Math.cos(((slice.startAngle + slice.endAngle) / 2) * Math.PI / 180)}
                      y={dimensions.height / 2 + (radius * 0.7) * Math.sin(((slice.startAngle + slice.endAngle) / 2) * Math.PI / 180)}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-white text-xs font-semibold pointer-events-none"
                    >
                      {slice.percentage.toFixed(0)}%
                    </text>
                  )}
                </g>
              )
            })}
          </svg>

          {showLegend && (
            <div ref={legendRef} className={cn("flex flex-col", legendBelow ? "w-full" : "flex-1 min-w-[200px]")}>
              {/* Show key names as column headers */}
              {detectedLabelKey && valueKey && slices.length > 0 && (
                <div className="grid grid-cols-[auto,1fr] gap-0.5 pb-1 border-b border-gray-200 mb-2">
                  <div className="w-2 flex-shrink-0"></div>
                  <div className="grid grid-cols-[3fr,auto] text-xs font-semibold text-gray-500 tracking-wider gap-0">
                    <span className="truncate min-w-0">{detectedLabelKey.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ').toUpperCase()}</span>
                    <span className="text-right whitespace-nowrap">{valueKey.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ').toUpperCase()}</span>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-1.5 overflow-y-auto max-h-[350px] pr-1">
                {slices.map((slice, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-[auto,1fr] gap-0.5 cursor-pointer group transition-opacity duration-300"
                    onMouseEnter={() => setHoveredSlice(index)}
                    onMouseLeave={() => setHoveredSlice(null)}
                    style={{ opacity: hoveredSlice !== null && hoveredSlice !== index ? 0.4 : 1 }}
                  >
                    <div
                      className="w-2 h-3.5 rounded-lg flex-shrink-0 mt-0.5 transition-transform duration-300"
                      style={{
                        backgroundColor: slice.color,
                        transform: hoveredSlice === index ? 'scale(1.2)' : 'scale(1)'
                      }}
                    />
                    <div className="grid grid-cols-[3fr,auto] items-start gap-6 group-hover:bg-gray-50 px-1 -mx-1 rounded">
                      <div className="text-sm text-muted-foreground overflow-hidden text-ellipsis capitalize leading-tight" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', hyphens: 'auto' }} title={String(slice.label).replace(/_/g, " ")}>
                        {String(slice.label).replace(/_/g, " ")}
                      </div>
                      <div className="text-sm font-semibold text-right whitespace-nowrap mt-0.5">
                        {slice.isNumeric ? (
                          <span className="block">{slice.percentage.toFixed(1)}%</span>
                        ) : (
                          <span className="block text-xs italic text-gray-500">{typeof slice.value === 'string' ? slice.value : 'N/A'}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface CustomDonutChartProps extends CustomPieChartProps { }

export const CustomDonutChart: React.FC<CustomDonutChartProps> = (props) => {
  // Donut chart is a pie chart with inner radius set to 50% by default
  return <CustomPieChart {...props} innerRadius={props.innerRadius || 50} />
}

interface CustomScatterPlotProps {
  data: DataPoint[]
  xKey?: string
  yKey?: string
  className?: string
  height?: number
  dotSize?: number
  fill?: string
  showGrid?: boolean
  showTooltip?: boolean
  showLegend?: boolean
  groupKey?: string
  xAxisLabel?: string
  yAxisLabel?: string
  isResponsive?: boolean  // Enable horizontal scroll when false (for expanded views)
}

export const CustomScatterPlot: React.FC<CustomScatterPlotProps> = ({
  data,
  xKey,
  yKey,
  className,
  height = 400,
  dotSize = 5,
  fill = "hsl(142, 52%, 37%)",
  showGrid = true,
  showTooltip = true,
  showLegend = false,
  groupKey,
  xAxisLabel,
  yAxisLabel,
  isResponsive = true  // Default: true (responsive, no horizontal scroll)
}) => {
  const [hoveredPoint, setHoveredPoint] = React.useState<number | null>(null)
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 })
  const containerRef = React.useRef<HTMLDivElement>(null)

  const detectKeys = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0 || !data[0] || typeof data[0] !== 'object') return { xKey: "", yKey: "", groups: [] }

    const keys = Object.keys(data[0])
    const numericKeys = keys.filter((key) => typeof data[0][key] === "number")
    const groups = groupKey ? Array.from(new Set(data.map(d => String(d[groupKey])))) : []

    return {
      xKey: xKey || numericKeys[0],
      yKey: yKey || numericKeys[1] || numericKeys[0],
      groups
    }
  }, [data, xKey, yKey, groupKey])

  const { xKey: detectedXKey, yKey: detectedYKey, groups } = detectKeys

  // Auto-generate X-axis label if not provided
  const autoXAxisLabel = React.useMemo(() => {
    if (xAxisLabel) return xAxisLabel
    if (!detectedXKey) return ""

    return detectedXKey
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  }, [xAxisLabel, detectedXKey])

  // Auto-generate Y-axis label if not provided
  const autoYAxisLabel = React.useMemo(() => {
    if (yAxisLabel) return yAxisLabel
    if (!detectedYKey) return ""

    return detectedYKey
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  }, [yAxisLabel, detectedYKey])

  React.useEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setDimensions({
            width: entry.contentRect.width,
            height,
          })
        }
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [height])

  const margins = {
    top: 20,
    right: 20,
    bottom: (xAxisLabel || autoXAxisLabel) ? 100 : 80,
    left: (yAxisLabel || autoYAxisLabel) ? 80 : 60
  }

  // Calculate minimum width needed based on data points
  const MIN_POINT_SPACING = 50 // Minimum horizontal spacing per data point
  const baseChartWidth = dimensions.width - margins.left - margins.right

  // Calculate minimum chart width based on number of unique X values
  const uniqueXValues = React.useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return 0
    const values = data.map(d => d[detectedXKey] as number).filter(v => typeof v === 'number' && !isNaN(v))
    return new Set(values).size
  }, [data, detectedXKey])

  const minChartWidth = Math.max(450, uniqueXValues * MIN_POINT_SPACING)

  // For scatter plots, always enforce minimum width to prevent overcrowding
  // Enable horizontal scroll when chart width exceeds container width
  const needsScroll = minChartWidth > baseChartWidth
  const chartWidth = Math.max(baseChartWidth, minChartWidth)
  const chartHeight = height - margins.top - margins.bottom

  const xRange = React.useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return { min: 0, max: 0 }
    const values = data.map(d => d[detectedXKey] as number).filter(v => typeof v === 'number' && !isNaN(v))
    if (values.length === 0) return { min: 0, max: 0 }
    return { min: Math.min(...values), max: Math.max(...values) }
  }, [data, detectedXKey])

  const yRange = React.useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return { min: 0, max: 0 }
    const values = data.map(d => d[detectedYKey] as number).filter(v => typeof v === 'number' && !isNaN(v))
    if (values.length === 0) return { min: 0, max: 0 }
    // Always start y-axis at 0
    return { min: 0, max: Math.max(...values) }
  }, [data, detectedYKey])

  const getX = (value: number) => {
    const baseX = needsScroll ? 0 : margins.left
    if (typeof value !== 'number' || isNaN(value)) return baseX + chartWidth / 2
    const range = xRange.max - xRange.min
    if (range === 0) return baseX + chartWidth / 2
    const result = baseX + ((value - xRange.min) / range) * chartWidth
    return isFinite(result) ? result : baseX + chartWidth / 2
  }
  const getY = (value: number) => {
    if (typeof value !== 'number' || isNaN(value)) return margins.top + chartHeight / 2
    const range = yRange.max - yRange.min
    if (range === 0) return margins.top + chartHeight / 2
    const result = margins.top + chartHeight - ((value - yRange.min) / range) * chartHeight
    return isFinite(result) ? result : margins.top + chartHeight / 2
  }

  const colors = [
    "hsl(142, 52%, 37%)",  // Green
    "hsl(24, 100%, 50%)",   // Orange
    "hsl(210, 100%, 50%)",  // Blue
    "hsl(340, 82%, 52%)",   // Pink/Red
    "hsl(45, 93%, 47%)",    // Yellow/Gold
    "hsl(271, 76%, 53%)",   // Purple
    "hsl(184, 77%, 34%)",   // Teal
    "hsl(320, 70%, 40%)"    // Magenta
  ]

  const getColor = (item: DataPoint, index: number) => {
    if (groupKey && item[groupKey]) {
      const groupIndex = groups.indexOf(String(item[groupKey]))
      return colors[groupIndex % colors.length]
    }
    return fill
  }

  const xTicks = React.useMemo(() => {
    const tickCount = 5
    return Array.from({ length: tickCount + 1 }, (_, i) => xRange.min + (xRange.max - xRange.min) * (i / tickCount))
  }, [xRange])

  const yTicks = React.useMemo(() => {
    const tickCount = 5
    return Array.from({ length: tickCount + 1 }, (_, i) => yRange.min + (yRange.max - yRange.min) * (i / tickCount))
  }, [yRange])

  const formatValue = (value: number) => {
    if (typeof value !== 'number' || isNaN(value)) return '0'
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
    return value.toFixed(1)
  }

  // Don't render chart until we have dimensions
  if (dimensions.width === 0) {
    return (
      <div ref={containerRef} className={cn("w-full", className)} style={{ minHeight: height }}>
        <div className="flex items-center justify-center" style={{ height: height }}>
          <div className="text-gray-400">Loading chart...</div>
        </div>
      </div>
    );
  }

  // Conditional rendering checks after all hooks - don't render if no data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null
  }

  if (!detectedXKey || !detectedYKey) {
    return null
  }

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      <div className="relative flex">
        {/* Sticky Left Y-axis section when scrollable */}
        {needsScroll && (
          <div className="flex-shrink-0 sticky left-0 z-10 bg-white" style={{ width: margins.left }}>
            <svg width={margins.left} height={height}>
              {/* Y-axis line */}
              <line
                x1={margins.left - 1}
                y1={margins.top}
                x2={margins.left - 1}
                y2={margins.top + chartHeight}
                stroke="#D5D7DA"
                strokeWidth="2"
              />
              {/* Y-axis ticks and labels */}
              {yTicks.map((tick, i) => (
                <text
                  key={i}
                  x={margins.left - 12}
                  y={getY(tick) + 5}
                  textAnchor="end"
                  className="fill-muted-foreground text-xs"
                >
                  {formatValue(tick)}
                </text>
              ))}
              {/* Y-axis label */}
              {(yAxisLabel || autoYAxisLabel) && (
                <text
                  x={-(margins.top + chartHeight / 2)}
                  y={15}
                  transform="rotate(-90)"
                  textAnchor="middle"
                  className="fill-foreground text-sm font-medium"
                >
                  {yAxisLabel || autoYAxisLabel}
                </text>
              )}
            </svg>
          </div>
        )}

        {/* Scrollable chart area */}
        <div className={cn("relative flex-1", needsScroll && "overflow-x-auto overflow-y-visible")}>
          <svg width={needsScroll ? chartWidth : dimensions.width} height={height} className="overflow-visible">
            {/* Grid lines */}
            {showGrid && (
              <>
                <g>
                  {yTicks.map((tick, i) => {
                    const startX = needsScroll ? 0 : margins.left
                    return (
                      <line
                        key={`h-${i}`}
                        x1={startX}
                        y1={getY(tick)}
                        x2={startX + chartWidth}
                        y2={getY(tick)}
                        stroke="#D5D7DA"
                        strokeWidth="1"
                        opacity="0.3"
                      />
                    )
                  })}
                </g>
                <g>
                  {xTicks.map((tick, i) => (
                    <line
                      key={`v-${i}`}
                      x1={getX(tick)}
                      y1={margins.top}
                      x2={getX(tick)}
                      y2={margins.top + chartHeight}
                      stroke="#D5D7DA"
                      strokeWidth="1"
                      opacity="0.2"
                    />
                  ))}
                </g>
              </>
            )}

            {/* Y-axis (only show when not scrollable, otherwise it's in sticky section) */}
            {!needsScroll && (
              <g>
                <line
                  x1={margins.left}
                  y1={margins.top}
                  x2={margins.left}
                  y2={margins.top + chartHeight}
                  stroke="#D5D7DA"
                  strokeWidth="2"
                />
                {yTicks.map((tick, i) => (
                  <text
                    key={i}
                    x={margins.left - 10}
                    y={getY(tick) + 5}
                    textAnchor="end"
                    className="fill-muted-foreground text-xs"
                  >
                    {formatValue(tick)}
                  </text>
                ))}
                {/* Y-axis label */}
                {(yAxisLabel || autoYAxisLabel) && (
                  <text
                    x={15}
                    y={margins.top + chartHeight / 2}
                    transform={`rotate(-90, 15, ${margins.top + chartHeight / 2})`}
                    textAnchor="middle"
                    className="fill-foreground text-sm font-medium"
                  >
                    {yAxisLabel || autoYAxisLabel}
                  </text>
                )}
              </g>
            )}

            {/* X-axis */}
            <g>
              <line
                x1={needsScroll ? 0 : margins.left}
                y1={margins.top + chartHeight}
                x2={(needsScroll ? 0 : margins.left) + chartWidth}
                y2={margins.top + chartHeight}
                stroke="#D5D7DA"
                strokeWidth="2"
              />
              {xTicks.map((tick, i) => (
                <text
                  key={i}
                  x={getX(tick)}
                  y={margins.top + chartHeight + 20}
                  textAnchor="middle"
                  className="fill-muted-foreground text-xs"
                >
                  {formatValue(tick)}
                </text>
              ))}
            </g>

            {/* Scatter points */}
            <g>
              {Array.isArray(data) && data.map((item, index) => {
                const x = getX(item[detectedXKey] as number)
                const y = getY(item[detectedYKey] as number)
                const color = getColor(item, index)

                return (
                  <g key={index}>
                    {showTooltip ? (
                      <Tooltip
                        title={
                          <div>
                            {groupKey && item[groupKey] && (
                              <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
                                {String(item[groupKey])}
                              </div>
                            )}
                            <div style={{ color: '#9ca3af', fontSize: 12 }}>
                              {detectedXKey}: {formatValue(item[detectedXKey] as number)}
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>
                              {detectedYKey}: {formatValue(item[detectedYKey] as number)}
                            </div>
                          </div>
                        }
                        placement="top"
                      >
                        <circle
                          cx={x}
                          cy={y}
                          r={hoveredPoint === index ? dotSize + 2 : dotSize}
                          fill={color}
                          className="transition-all cursor-pointer"
                          opacity={hoveredPoint === null || hoveredPoint === index ? 1 : 0.5}
                          onMouseEnter={() => setHoveredPoint(index)}
                          onMouseLeave={() => setHoveredPoint(null)}
                        />
                      </Tooltip>
                    ) : (
                      <circle
                        cx={x}
                        cy={y}
                        r={hoveredPoint === index ? dotSize + 2 : dotSize}
                        fill={color}
                        className="transition-all"
                        opacity={hoveredPoint === null || hoveredPoint === index ? 1 : 0.5}
                        onMouseEnter={() => setHoveredPoint(index)}
                        onMouseLeave={() => setHoveredPoint(null)}
                      />
                    )}
                  </g>
                )
              })}
            </g>
          </svg>
        </div>
      </div>

      {/* X-axis label (outside scrollable area) */}
      {(xAxisLabel || autoXAxisLabel) && (
        <div className="text-center mt-2">
          <span className="text-sm font-medium text-foreground">
            {xAxisLabel || autoXAxisLabel}
          </span>
        </div>
      )}

      {showLegend && groupKey && groups.length > 0 && (
        <div className="flex flex-wrap justify-end gap-4 mt-4">
          {groups.map((group, i) => (
            <div key={group} className="flex items-center gap-1">
              <div
                className="w-2 h-3.5 rounded-lg"
                style={{ backgroundColor: colors[i % colors.length] }}
              />
              <span className="text-sm text-muted-foreground capitalize">
                {String(group).replace(/_/g, " ")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface CustomHorizontalBarChartProps extends Omit<CustomBarChartProps, "barWidth"> {
  barHeight?: number
}

interface CustomMultiHorizontalBarChartProps extends Omit<CustomHorizontalBarChartProps, "dataKey"> {
  dataKey?: string | string[]
}

export const CustomMultiHorizontalBarChart: React.FC<CustomMultiHorizontalBarChartProps> = ({
  data,
  dataKey,
  xAxisKey,
  className,
  height = 400,
  barHeight = 20,
  minBarHeight = 3,
  fill = "hsl(142, 52%, 37%)",
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  multiBar = true
}) => {
  const [hoveredItem, setHoveredItem] = React.useState<{ dataIndex: number; keyIndex: number } | null>(null)
  const [hoveredLegend, setHoveredLegend] = React.useState<string | null>(null)
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 })
  const [error, setError] = React.useState<string | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const detectKeys = React.useMemo(() => {
    if (!data || data.length === 0 || !data[0] || typeof data[0] !== 'object') return { xKey: "", yKeys: [] }

    const keys = Object.keys(data[0])
    const numericKeys = keys.filter((key) => typeof data[0][key] === "number")
    const nonNumericKeys = keys.filter((key) => typeof data[0][key] !== "number")

    return {
      xKey: xAxisKey || nonNumericKeys[0] || keys[0],
      yKeys: numericKeys
    }
  }, [data, xAxisKey])

  const { xKey, yKeys } = detectKeys

  React.useEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          const barGroupHeight = barHeight * yKeys.length + 20
          const calculatedHeight = Math.max(
            height,
            data.length * barGroupHeight + 150
          )
          setDimensions({
            width: entry.contentRect.width,
            height: calculatedHeight
          })
        }
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [height, data.length, barHeight, yKeys.length])

  const margins = { top: 40, right: 150, bottom: 60, left: 120 }
  const chartWidth = dimensions.width - margins.left - margins.right
  const chartHeight = dimensions.height - margins.top - margins.bottom

  const maxValue = React.useMemo(() => {
    let max = 0
    data.forEach((item) => {
      yKeys.forEach((key) => {
        const value = item[key] as number
        if (value > max) max = value
      })
    })
    return Math.max(Math.ceil(max * 1.1), 10)
  }, [data, yKeys])

  const barGroupHeight = chartHeight / data.length
  const individualBarHeight = Math.min(barHeight, (barGroupHeight - 10) / yKeys.length)
  const barGroupPadding = (barGroupHeight - individualBarHeight * yKeys.length) / 2

  const colors = [
    "hsl(142, 52%, 37%)",
    "hsl(24, 100%, 50%)",
    "hsl(80, 70%, 30%)",
    "hsl(240, 40%, 60%)",
    "hsl(54, 100%, 62%)"
  ]

  const xTicks = React.useMemo(() => {
    const tickCount = 5
    return Array.from({ length: tickCount + 1 }, (_, i) => (maxValue / tickCount) * i)
  }, [maxValue])

  const formatValue = (value: number | undefined, useAbbreviation: boolean = true) => {
    if (value === undefined || value === null) return '0'
    if (useAbbreviation) {
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
      // For values less than 1, show 2 decimal places; otherwise show appropriate decimals
      if (value < 1 && value > 0) return value.toFixed(2)
      if (value < 10) return value.toFixed(1)
      return value.toFixed(0)
    }
    // For tooltip (not abbreviated), show more precision for small values
    if (value < 1 && value > 0) return value.toFixed(2)
    if (value < 10) return value.toFixed(1)
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
  }

  const formatLabel = (value: string | number | undefined) => {
    if (value === undefined || value === null) return ''
    const str = String(value)
    return str.length > 15 ? str.substring(0, 15) + "..." : str
  }

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      <div className="relative">
        <svg width={dimensions.width} height={dimensions.height} className="overflow-visible">
          {/* Horizontal grid lines */}
          {showGrid && (
            <g>
              {Array.isArray(data) && data.map((_, i) => {
                const y = margins.top + i * barGroupHeight + barGroupHeight / 2
                return (
                  <line
                    key={`h-${i}`}
                    x1={margins.left}
                    y1={y}
                    x2={margins.left + chartWidth}
                    y2={y}
                    stroke="hsl(var(--border))"
                    strokeWidth="1"
                    opacity="0.2"
                  />
                )
              })}
            </g>
          )}

          {/* Vertical grid lines */}
          {showGrid && (
            <g>
              {xTicks.map((tick, i) => (
                <line
                  key={`v-${i}`}
                  x1={margins.left + (tick / maxValue) * chartWidth}
                  y1={margins.top}
                  x2={margins.left + (tick / maxValue) * chartWidth}
                  y2={margins.top + chartHeight}
                  stroke="#D5D7DA"
                  strokeWidth="1"
                  opacity="0.3"
                />
              ))}
            </g>
          )}

          {/* Y-axis labels */}
          <g>
            <line
              x1={margins.left}
              y1={margins.top}
              x2={margins.left}
              y2={margins.top + chartHeight}
              stroke="#D5D7DA"
              strokeWidth="2"
            />
            {Array.isArray(data) && data.map((item, i) => {
              const y = margins.top + i * barGroupHeight + barGroupHeight / 2
              return (
                <text
                  key={i}
                  x={margins.left - 10}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="fill-muted-foreground text-xs"
                >
                  {formatLabel(item[xKey])}
                </text>
              )
            })}
          </g>

          {/* X-axis */}
          <g>
            <line
              x1={margins.left}
              y1={margins.top + chartHeight}
              x2={margins.left + chartWidth}
              y2={margins.top + chartHeight}
              stroke="#D5D7DA"
              strokeWidth="2"
            />
            {xTicks.map((tick, i) => (
              <text
                key={i}
                x={margins.left + (tick / maxValue) * chartWidth}
                y={margins.top + chartHeight + 20}
                textAnchor="middle"
                className="fill-muted-foreground text-xs"
              >
                {formatValue(tick)}
              </text>
            ))}
          </g>

          {/* Bars */}
          <g>
            {Array.isArray(data) && data.map((item, dataIndex) => {
              const yBase = margins.top + dataIndex * barGroupHeight + barGroupPadding

              return (
                <g key={dataIndex}>
                  {yKeys.map((key, keyIndex) => {
                    const value = item[key] as number
                    const barWidth = (value / maxValue) * chartWidth
                    const y = yBase + keyIndex * individualBarHeight
                    const isHovered = hoveredItem?.dataIndex === dataIndex && hoveredItem?.keyIndex === keyIndex
                    const legendOpacity = hoveredLegend && hoveredLegend !== key ? 0.3 : 1
                    const finalOpacity = hoveredItem && !isHovered ? 0.5 : legendOpacity

                    return (
                      <g key={`${dataIndex}-${keyIndex}`}>
                        {showTooltip ? (
                          <Tooltip
                            title={
                              <div>
                                <div style={{ marginBottom: 4, color: '#9ca3af', fontSize: 12 }}>
                                  {String(item[xKey])}
                                </div>
                                <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
                                  {String(key).replace(/_/g, " ").charAt(0).toUpperCase() + String(key).replace(/_/g, " ").slice(1)}
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>
                                  {formatValue(value, false)}
                                </div>
                              </div>
                            }
                            placement="top"
                          >
                            <rect
                              x={margins.left}
                              y={y}
                              width={barWidth}
                              height={individualBarHeight - 2}
                              fill={colors[keyIndex % colors.length]}
                              opacity={finalOpacity}
                              className="transition-opacity duration-200 cursor-pointer"
                              onMouseEnter={() => setHoveredItem({ dataIndex, keyIndex })}
                              onMouseLeave={() => setHoveredItem(null)}
                              rx={2}
                            />
                          </Tooltip>
                        ) : (
                          <rect
                            x={margins.left}
                            y={y}
                            width={barWidth}
                            height={individualBarHeight - 2}
                            fill={colors[keyIndex % colors.length]}
                            opacity={finalOpacity}
                            className="transition-opacity duration-200 cursor-pointer"
                            onMouseEnter={() => setHoveredItem({ dataIndex, keyIndex })}
                            onMouseLeave={() => setHoveredItem(null)}
                            rx={2}
                          />
                        )}
                      </g>
                    )
                  })}
                </g>
              )
            })}
          </g>

        </svg>
      </div>

      {showLegend && (
        <div className="flex flex-wrap justify-end gap-4 mt-4">
          {yKeys.map((key, i) => {
            const isHighlighted = hoveredLegend === null || hoveredLegend === key
            const opacity = hoveredLegend && hoveredLegend !== key ? 0.4 : 1

            return (
              <div
                key={key}
                className="flex items-center gap-1 cursor-pointer transition-opacity duration-300"
                onMouseEnter={() => setHoveredLegend(key)}
                onMouseLeave={() => setHoveredLegend(null)}
                style={{ opacity }}
              >
                <div
                  className="w-2 h-3.5 rounded-lg transition-all duration-300"
                  style={{
                    backgroundColor: colors[i % colors.length],
                    transform: isHighlighted && hoveredLegend ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
                <span className="text-sm text-muted-foreground capitalize">
                  {String(key).replace(/_/g, " ")}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export const CustomHorizontalBarChart: React.FC<CustomHorizontalBarChartProps> = ({
  data,
  dataKey,
  xAxisKey,
  className,
  height = 400,
  barHeight = 32,
  minBarHeight = 3,
  fill = "hsl(142, 52%, 37%)",
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  multiBar = false
}) => {
  const [hoveredBar, setHoveredBar] = React.useState<number | null>(null)
  const [hoveredLegend, setHoveredLegend] = React.useState<string | null>(null)
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 })
  const containerRef = React.useRef<HTMLDivElement>(null)

  const detectKeys = React.useMemo(() => {
    if (!data || data.length === 0 || !data[0] || typeof data[0] !== 'object') return { xKey: "", yKeys: [] }

    const keys = Object.keys(data[0])
    const numericKeys = keys.filter((key) => typeof data[0][key] === "number")
    const nonNumericKeys = keys.filter((key) => typeof data[0][key] !== "number")

    return {
      xKey: xAxisKey || nonNumericKeys[0] || keys[0],
      yKeys: multiBar ? numericKeys : [dataKey || numericKeys[0]]
    }
  }, [data, xAxisKey, dataKey, multiBar])

  const { xKey, yKeys } = detectKeys

  React.useEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          const calculatedHeight = Math.max(
            height,
            data.length * (multiBar ? barHeight * yKeys.length + 20 : barHeight + 20) + 100
          )
          setDimensions({
            width: entry.contentRect.width,
            height: calculatedHeight
          })
        }
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [height, data.length, barHeight, multiBar, yKeys.length])

  const margins = { top: 20, right: 20, bottom: 60, left: 120 }
  const chartWidth = dimensions.width - margins.left - margins.right
  const chartHeight = dimensions.height - margins.top - margins.bottom

  const maxValue = React.useMemo(() => {
    let max = 0
    data.forEach((item) => {
      yKeys.forEach((key) => {
        const value = item[key] as number
        if (value > max) max = value
      })
    })
    return Math.max(Math.ceil(max * 1.1), 10)
  }, [data, yKeys])

  const barGroupHeight = chartHeight / data.length
  const actualBarHeight = Math.min(barHeight, barGroupHeight * 0.8)
  const barSpacing = multiBar ? actualBarHeight / yKeys.length : actualBarHeight

  const colors = [
    "hsl(142, 52%, 37%)",
    "hsl(24, 100%, 50%)",
    "hsl(80, 70%, 30%)",
    "hsl(240, 40%, 60%)",
    "hsl(54, 100%, 62%)"
  ]

  const xTicks = React.useMemo(() => {
    const tickCount = 5
    return Array.from({ length: tickCount + 1 }, (_, i) => (maxValue / tickCount) * i)
  }, [maxValue])

  const formatValue = (value: number | undefined, useAbbreviation: boolean = true) => {
    if (value === undefined || value === null) return '0'
    if (useAbbreviation) {
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
      // For values less than 1, show 2 decimal places; otherwise show appropriate decimals
      if (value < 1 && value > 0) return value.toFixed(2)
      if (value < 10) return value.toFixed(1)
      return value.toFixed(0)
    }
    // For tooltip (not abbreviated), show more precision for small values
    if (value < 1 && value > 0) return value.toFixed(2)
    if (value < 10) return value.toFixed(1)
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
  }

  const formatLabel = (value: string | number | undefined) => {
    if (value === undefined || value === null) return ''
    const str = String(value)
    return str.length > 15 ? str.substring(0, 15) + "..." : str
  }

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      <div className="relative">
        <svg width={dimensions.width} height={dimensions.height} className="overflow-visible">
          {/* Vertical grid lines */}
          {showGrid && (
            <g>
              {xTicks.map((tick, i) => (
                <line
                  key={`v-${i}`}
                  x1={margins.left + (tick / maxValue) * chartWidth}
                  y1={margins.top}
                  x2={margins.left + (tick / maxValue) * chartWidth}
                  y2={margins.top + chartHeight}
                  stroke="#D5D7DA"
                  strokeWidth="1"
                  opacity="0.3"
                />
              ))}
            </g>
          )}

          {/* Horizontal grid lines */}
          {showGrid && (
            <g>
              {Array.isArray(data) && data.map((_, i) => (
                <line
                  key={`h-${i}`}
                  x1={margins.left}
                  y1={margins.top + i * barGroupHeight + barGroupHeight / 2}
                  x2={margins.left + chartWidth}
                  y2={margins.top + i * barGroupHeight + barGroupHeight / 2}
                  stroke="hsl(var(--border))"
                  strokeWidth="1"
                  opacity="0.2"
                />
              ))}
            </g>
          )}

          {/* Y-axis with labels */}
          <g>
            <line
              x1={margins.left}
              y1={margins.top}
              x2={margins.left}
              y2={margins.top + chartHeight}
              stroke="#D5D7DA"
              strokeWidth="2"
            />
            {Array.isArray(data) && data.map((item, i) => {
              const y = margins.top + i * barGroupHeight + barGroupHeight / 2
              return (
                <text
                  key={i}
                  x={margins.left - 10}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="fill-muted-foreground text-xs"
                >
                  {formatLabel(item[xKey])}
                </text>
              )
            })}
          </g>

          {/* X-axis with labels */}
          <g>
            <line
              x1={margins.left}
              y1={margins.top + chartHeight}
              x2={margins.left + chartWidth}
              y2={margins.top + chartHeight}
              stroke="#D5D7DA"
              strokeWidth="2"
            />
            {xTicks.map((tick, i) => (
              <text
                key={i}
                x={margins.left + (tick / maxValue) * chartWidth}
                y={margins.top + chartHeight + 20}
                textAnchor="middle"
                className="fill-muted-foreground text-xs"
              >
                {formatValue(tick)}
              </text>
            ))}
          </g>

          {/* Top border line */}
          <line
            x1={margins.left}
            y1={margins.top}
            x2={margins.left + chartWidth}
            y2={margins.top}
            stroke="hsl(var(--border))"
            strokeWidth="1"
            opacity="0.5"
          />

          {/* Right border line */}
          <line
            x1={margins.left + chartWidth}
            y1={margins.top}
            x2={margins.left + chartWidth}
            y2={margins.top + chartHeight}
            stroke="hsl(var(--border))"
            strokeWidth="1"
            opacity="0.5"
          />

          <g>
            {Array.isArray(data) && data.map((item, dataIndex) => (
              <g key={dataIndex}>
                {yKeys.map((key, keyIndex) => {
                  const value = item[key] as number
                  const barY = margins.top + dataIndex * barGroupHeight + barGroupHeight / 2
                  const barX = margins.left
                  // Ensure minimum bar width for visibility, even for zero or small values
                  const calculatedWidth = (value / maxValue) * chartWidth
                  const barWidth = value === 0 || value === null || value === undefined
                    ? minBarHeight
                    : Math.max(calculatedWidth, minBarHeight)
                  const color = multiBar ? colors[keyIndex % colors.length] : fill
                  const yOffset = multiBar
                    ? -((yKeys.length * barSpacing) / 2) + keyIndex * barSpacing
                    : -actualBarHeight / 2
                  const legendOpacity = hoveredLegend && hoveredLegend !== key ? 0.3 : 1
                  const barHoverOpacity = hoveredBar === null || hoveredBar === dataIndex ? 1 : 0.5
                  const finalOpacity = Math.min(legendOpacity, barHoverOpacity)

                  return (
                    <g key={`${dataIndex}-${keyIndex}`}>
                      {showTooltip ? (
                        <Tooltip
                          title={
                            <div>
                              <div style={{ marginBottom: 4, color: '#9ca3af', fontSize: 12 }}>
                                {String(item[xKey])}
                              </div>
                              <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
                                {String(key).replace(/_/g, " ").charAt(0).toUpperCase() + String(key).replace(/_/g, " ").slice(1)}
                              </div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>
                                {formatValue(value, false)}
                              </div>
                            </div>
                          }
                          placement="top"
                        >
                          <path
                            d={`
                              M ${barX} ${barY + yOffset}
                              L ${barX + barWidth - 4} ${barY + yOffset}
                              Q ${barX + barWidth} ${barY + yOffset}, ${barX + barWidth} ${barY + yOffset + 4}
                              L ${barX + barWidth} ${barY + yOffset + (multiBar ? barSpacing * 0.8 : actualBarHeight) - 4}
                              Q ${barX + barWidth} ${barY + yOffset + (multiBar ? barSpacing * 0.8 : actualBarHeight)}, ${barX + barWidth - 4} ${barY + yOffset + (multiBar ? barSpacing * 0.8 : actualBarHeight)}
                              L ${barX} ${barY + yOffset + (multiBar ? barSpacing * 0.8 : actualBarHeight)}
                              Z
                            `}
                            fill={color}
                            className="transition-opacity cursor-pointer"
                            opacity={finalOpacity}
                            onMouseEnter={() => setHoveredBar(dataIndex)}
                            onMouseLeave={() => setHoveredBar(null)}
                          />
                        </Tooltip>
                      ) : (
                        <path
                          d={`
                            M ${barX} ${barY + yOffset}
                            L ${barX + barWidth - 4} ${barY + yOffset}
                            Q ${barX + barWidth} ${barY + yOffset}, ${barX + barWidth} ${barY + yOffset + 4}
                            L ${barX + barWidth} ${barY + yOffset + (multiBar ? barSpacing * 0.8 : actualBarHeight) - 4}
                            Q ${barX + barWidth} ${barY + yOffset + (multiBar ? barSpacing * 0.8 : actualBarHeight)}, ${barX + barWidth - 4} ${barY + yOffset + (multiBar ? barSpacing * 0.8 : actualBarHeight)}
                            L ${barX} ${barY + yOffset + (multiBar ? barSpacing * 0.8 : actualBarHeight)}
                            Z
                          `}
                          fill={color}
                          className="transition-opacity"
                          opacity={finalOpacity}
                          onMouseEnter={() => setHoveredBar(dataIndex)}
                          onMouseLeave={() => setHoveredBar(null)}
                        />
                      )}
                    </g>
                  )
                })}
              </g>
            ))}
          </g>
        </svg>
      </div>

      {showLegend && multiBar && (
        <div className="flex flex-wrap justify-end gap-4 mt-4">
          {yKeys.map((key, i) => {
            const isHighlighted = hoveredLegend === null || hoveredLegend === key
            const opacity = hoveredLegend && hoveredLegend !== key ? 0.4 : 1

            return (
              <div
                key={key}
                className="flex items-center gap-1 cursor-pointer transition-opacity duration-300"
                onMouseEnter={() => setHoveredLegend(key)}
                onMouseLeave={() => setHoveredLegend(null)}
                style={{ opacity }}
              >
                <div
                  className="w-2 h-3.5 rounded-lg transition-all duration-300"
                  style={{
                    backgroundColor: colors[i % colors.length],
                    transform: isHighlighted && hoveredLegend ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
                <span className="text-sm text-muted-foreground capitalize">
                  {String(key).replace(/_/g, " ")}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}